<script lang="ts">
  import { onMount, onDestroy, tick } from 'svelte'
  import { get } from 'svelte/store'
  import { fileStore, documents, activeTab, openTabs } from '$lib/stores/fileStore'
  import { state as explorerState, resetRenameFlags } from '$lib/stores/fileExplorerState.svelte'
  import { vaultStore } from '$lib/stores/vaultStore'
  import modal from '$lib/stores/modalStore'
  import { toast } from '$lib/stores/toast'
  import { fileSearchStore, fileSearchStats } from '$lib/stores/fileSearchStoreOptimized'
  import { searchStore } from '$lib/stores/searchStore'
  import { updateExplorerSettings } from '$lib/stores/uiSettingsStore'
  import type { DocumentInfo } from '$lib/storage/types'
  import {
    Plus, File, Folder, Search, ChevronRight, ChevronDown,
    Trash2, Edit2, MoreVertical, FileText, Clock,
    ChevronsUpDown, ChevronsDown, ChevronsUp
  } from 'lucide-svelte'
import VaultDropdown from './VaultDropdown.svelte'
import { getFileSystemWatcher } from '$lib/services/FileSystemWatcher'
import FileNodeComponent from './FileNode.svelte'
import FolderNodeComponent from './FolderNode.svelte'
import RootFileWrapper from './RootFileWrapper.svelte'
import templateImages from '../../template-images.json' assert { type: 'json' }
  
  /**
   * FileExplorer: Advanced file explorer with tree structure
   * Single Responsibility: File navigation and organization
   */
  
  // Check if we're in Electron environment
  const isElectron = typeof window !== 'undefined' && window.api?.showContextMenu !== undefined
  
  interface Props {
    class?: string
    searchQuery?: string
  }
  
  let { class: className = '', searchQuery = '' }: Props = $props()
  
  export interface FileNode {
    id: string
    name: string
    type: 'file' | 'folder'
    children?: FileNode[]
    document?: DocumentInfo
    isImage?: boolean
    expanded?: boolean
    isReadError?: boolean
    extension?: string
  }
  
  const TEMPLATE_IMAGE_FILENAMES = (templateImages as string[])
  const DEMO_TEMPLATE_IMAGE_PATHS = TEMPLATE_IMAGE_FILENAMES.map(name => `이미지/${name}`)

  let fileTree = $state<FileNode[]>([])
  let editingNodeId = $state<string | null>(null)
  let editingTitle = $state('')
  let editingOriginalNode = $state<FileNode | null>(null) // Store original node for rename
  let editingDuplicateWarning = $state('') // Warning message for duplicate names
  let duplicateWarningTimestamp = $state<number | null>(null) // When warning was first shown
  let duplicateWarningTimeout = $state<ReturnType<typeof setTimeout> | null>(null) // Timeout for clearing warning
  const DUPLICATE_WARNING_MIN_DURATION = 2500 // Minimum duration to show warning (ms)
  let isProcessingEdit = $state(false) // Prevent duplicate execution
  let contextMenuNode = $state<FileNode | null>(null)
  let contextMenuPosition = $state({ x: 0, y: 0 })
  let contextMenuType = $state<'node' | 'blank'>('node')
  
  // Store folder structure separately to persist it
  let folderStructure = $state<FileNode[]>([])
  
  // Multi-selection state
  let selectedFiles = $state<Set<string>>(new Set())
  let lastSelectedFile = $state<string | null>(null)
  
  // Keyboard navigation state
  let focusedNodeId = $state<string | null>(null)
  
  // Drag and drop state
  let draggedFiles = $state<Set<string>>(new Set())
  let dropTargetId = $state<string | null>(null)
  let isDragging = $state(false)
  let dropTargetRoot = $state(false)
  let dropPosition = $state<number | null>(null)
  
  let documentList = $derived($documents)
  let currentTab = $derived($activeTab)
  let tabs = $derived($openTabs)
  
  // Subscribe to fileStore to trigger re-renders when tabs change
  let fileStoreState = $derived($fileStore)
  
  // Search state
  let searchMode = $state(false)
  let searchInput = $state('')
  let searchInputEl: HTMLInputElement | null = null
  let searchResults = $derived($fileSearchStore)
  let searchStats = $derived($fileSearchStats)
  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
  
  // Loading state for vault scan
  let isScanning = $state(false)
  let scanProgress = $state<{ current: number; total: number; currentPath: string } | null>(null)
  
  // Track if vault is changing to prevent empty state flash
  let isVaultChanging = $state(false)
  
  // Load/Save folder structure from localStorage
  const FOLDER_STRUCTURE_KEY = 'korean-grammar-folder-structure'
  
  function saveFolderStructure() {
    // Recursively save the entire tree structure
    function serializeNode(node: FileNode): any {
      if (node.type === 'folder') {
        return {
          id: node.id,
          name: node.name,
          type: 'folder',
          expanded: node.expanded,
          children: node.children?.map(serializeNode) || []
        }
      } else {
        return {
          id: node.id,
          name: node.name,
          type: 'file'
        }
      }
    }
    
    const serialized = fileTree.map(serializeNode)
    localStorage.setItem(FOLDER_STRUCTURE_KEY, JSON.stringify(serialized))
  }
  
  function loadFolderStructure(): any[] {
    try {
      const saved = localStorage.getItem(FOLDER_STRUCTURE_KEY)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  }
  
  // Track if file explorer has focus
  let explorerEl: HTMLDivElement
  let hasFocus = $state(false)
  
  // Track scrolling state
  let fileTreeEl: HTMLDivElement
  let isScrolling = $state(false)
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null
  
  // No need for automatic rebuild based on documents
  // We'll rely on explicit refresh events that read from file system
  
  // Watch for active tab changes - simpler approach
  let previousActiveId = ''
  $effect(() => {
    const activeId = currentTab?.id || ''
    if (activeId !== previousActiveId) {
      previousActiveId = activeId
      // Just trigger reactivity, don't modify state
    }
  })
  
  // Build initial file tree
  let fsWatcher: ReturnType<typeof getFileSystemWatcher> | null = null
  let refreshDebounceTimer: ReturnType<typeof setTimeout> | null = null
  let isRefreshing = false  // Prevent concurrent refreshes

  // Track previous vault path to detect changes
  let previousVaultPath: string | null = null
  
  // Watch for vault changes and restart watcher
  $effect(async () => {
    const vault = get(vaultStore)
    const currentVaultPath = vault.vaultPath || null
    
    // Check if vault path has changed (including initial load after mount)
    if (vault.isInitialized && currentVaultPath && currentVaultPath !== previousVaultPath) {
      // Skip if this is the initial load (onMount will handle it)
      if (previousVaultPath === null) {
        previousVaultPath = currentVaultPath
        return
      }
      
      previousVaultPath = currentVaultPath
      
      // Stop existing watcher if any
      if (fsWatcher) {
        fsWatcher.stop()
        fsWatcher = null
      }
      
      // Set vault changing flag to prevent empty state flash
      isVaultChanging = true
      
      // Clear all state completely - trigger reactivity with new arrays
      fileTree = [] // Clear first
      folderStructure = []
      selectedFiles = new Set()
      focusedNodeId = null
      lastSelectedFile = null
      editingNodeId = null
      contextMenuNode = null
      draggedFiles = new Set()
      searchMode = false
      searchInput = ''
      
      // Force reactivity update by reassigning
      fileTree = [...fileTree]
      
      // Show scanning state
      isScanning = true
      scanProgress = { current: 0, total: 100, currentPath: 'Loading new vault...' }
      
      // Use tick to ensure UI updates
      await tick()
      
      // Rebuild file tree when vault changes
      // Reload documents from new vault
      await fileStore.loadDocuments()
      
      // Get new file system watcher
      fsWatcher = getFileSystemWatcher()
      
      // Build tree with full scan for vault change
      await buildVaultFileTree(false, true)
      
      // Clear scanning state  
      isScanning = false
      scanProgress = null
      isVaultChanging = false // Clear vault changing flag
      
      // Force another update to ensure tree is shown
      fileTree = [...fileTree]
      
      // Force UI update
      await tick()
    }
  })
  
  onMount(async () => {

    // Set up progress listener for scan
    if (typeof window !== 'undefined' && window.api?.onScanProgress) {
      window.api.onScanProgress((progress: any) => {
        if (isScanning) {
          scanProgress = progress
        }
      })
    }

    // Load documents and build tree in parallel for faster initial load
    // Pass skipCheck=true to always rebuild on mount (even during rename)
    await Promise.all([
      fileStore.loadDocuments(),
      buildFileTree(true)
    ])
    
    // Get file system watcher instance but don't start it here
    // MainEditor handles starting the watcher to avoid duplicates
    fsWatcher = getFileSystemWatcher()
    
    // Listen for vault change event
    const handleVaultChange = async () => {
      const vault = get(vaultStore)
      const currentVaultPath = vault.vaultPath || null
      
      if (currentVaultPath && currentVaultPath !== previousVaultPath) {
        previousVaultPath = currentVaultPath
        
        // Set vault changing flag to prevent empty state flash
        isVaultChanging = true
        
        // Clear and rebuild
        fileTree = []
        await tick()
        
        await fileStore.loadDocuments()
        await buildVaultFileTree(false, true)
        
        isVaultChanging = false // Clear vault changing flag
      }
    }
    window.addEventListener('vault-changed', handleVaultChange)

    // Listen for file rename events (targeted update without full rebuild)
    const handleFileRenamed = (event: Event) => {
      // Prevent duplicate processing from multiple FileExplorer instances
      if (explorerState.isProcessingRename) {
        return
      }
      explorerState.isProcessingRename = true
      explorerState.lastRenameTimestamp = Date.now()  // Record rename time

      const { oldPath, newPath, newTitle } = (event as CustomEvent).detail
      explorerState.lastRenamedOldPath = oldPath
      explorerState.lastRenamedNewPath = newPath

      // Skip rebuild on next document count change - we're handling it here
      explorerState.skipNextRebuild = true

      // DON'T update documents here - it triggers $effect which can reset skipNextRebuild
      // Documents will be updated by fileStore's delayed update (100ms)
      // or naturally when the file list is refreshed

      // Find and update the specific node in the tree
      function updateNodeInTree(nodes: FileNode[], parentIndex: number[] = []): boolean {
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i]

          // Check if this is the file we're looking for
          // Match with and without extension
          const isMatch = node.type === 'file' && (
            node.id === oldPath ||
            node.id === `${oldPath}.md` ||
            node.id.replace(/\.md$/, '') === oldPath
          )

        if (isMatch) {

            const displayName = (() => {
              const lastSlash = newPath.lastIndexOf('/')
              return lastSlash >= 0 ? newPath.slice(lastSlash + 1) : newPath
            })()

            const updatedDocument = node.document
              ? { ...node.document, id: newPath, title: newTitle || displayName }
              : undefined

            // Create a new node object instead of mutating the existing one
            // This prevents Svelte reactivity issues with proxies
            nodes[i] = {
              ...node,
              id: newPath,
              name: displayName,
              document: updatedDocument
            }

            return true
          }

          // Recursively check children
          if (node.children) {
            const childUpdated = updateNodeInTree(node.children, [...parentIndex, i])
            if (childUpdated) {
              return true
            }
          }
        }
        return false
      }

      // Update the node - Svelte 5's $state will track the changes automatically
      const found = updateNodeInTree(fileTree)

      if (found) {
        // Successfully updated the node
        // Keep the flags set for a long period to prevent cached buildVaultFileTree from interfering
        // BuildVaultFileTree uses cache and can complete after 100ms
        setTimeout(() => {
          explorerState.isProcessingRename = false
          explorerState.skipNextRebuild = false
        }, 1000)  // 1 second to ensure all cached async operations complete
      } else {
        // If not found, immediately reset flags and trigger a full rebuild
        explorerState.skipNextRebuild = false
        explorerState.isProcessingRename = false

        // Force immediate rebuild to restore correct state
        queueMicrotask(() => {
          buildFileTree(true) // skipCheck=true to bypass skipNextRebuild
        })
      }
    }
    window.addEventListener('file-renamed', handleFileRenamed)

    // Listen for file explorer refresh event with debouncing
    const handleRefresh = async (event?: Event) => {
      // Check if this is a forced refresh (e.g., from file rename)
      const detail = (event as CustomEvent)?.detail || {}
      const isForced = detail.force === true
      const forceFullScan = detail.forceFullScan === true
      const preserveActiveTabId = detail.preserveActiveTabId
      const skipLoadDocuments = detail.skipLoadDocuments === true

      if (editingNodeId && !detail.allowDuringEdit) {
        return
      }

      // Prevent concurrent refreshes
      if (isRefreshing && !isForced) {
        return
      }

      // Clear existing debounce timer
      if (refreshDebounceTimer) {
        clearTimeout(refreshDebounceTimer)
      }

      // For forced refreshes, use shorter debounce and bypass editing check
      const debounceTime = isForced ? 50 : 500

      // Set new debounce timer - increased debounce to avoid excessive scanning
      refreshDebounceTimer = setTimeout(async () => {
        refreshDebounceTimer = null

        // Double-check if already refreshing
        if (isRefreshing) {
          return
        }

        // For forced refresh, bypass editingNodeId check
        // Only refresh if not currently performing an action and not already scanning
        if ((isForced || !editingNodeId) && !contextMenuNode && !isScanning) {
          try {
            isRefreshing = true  // Set flag to prevent concurrent refreshes

            // Clear editingNodeId for forced refresh to ensure UI updates
            if (isForced && editingNodeId) {
              editingNodeId = null
            }

            // Store current activeTabId before refresh
            const currentActiveTabId = preserveActiveTabId || get(fileStore).activeTabId

            // Reload documents first to get latest file list (skip if already updated)
            if (!skipLoadDocuments) {
              await fileStore.loadDocuments()
            }

            // Then build tree with incremental update hint - disable full scan for file system changes
            const incremental = forceFullScan ? false : !forceFullScan
            const useFullScan = forceFullScan ? false : forceFullScan
            await buildVaultFileTree(incremental, useFullScan, isForced)

            // Restore activeTabId if it was changed during refresh
            const afterRefreshActiveTabId = get(fileStore).activeTabId
            if (currentActiveTabId && afterRefreshActiveTabId !== currentActiveTabId) {
              fileStore.setActiveTab(currentActiveTabId)
            }
          } finally {
            isRefreshing = false  // Always clear flag
          }
        }
      }, debounceTime)
    }
    window.addEventListener('file-explorer-refresh', handleRefresh)
    
    // Don't listen for file-system-changed here to avoid duplicate refresh
    // MainEditor already handles this and triggers file-explorer-refresh
    
    // Listen for clear focus event from editor
    const handleClearFocus = () => {
      focusedNodeId = null
      selectedFiles.clear()
      selectedFiles = new Set(selectedFiles) // Trigger reactivity
    }
    
    window.addEventListener('clear-explorer-focus', handleClearFocus)
    
    // Don't add watcher listener here to avoid duplicate triggers
    // MainEditor handles file watching and triggers file-explorer-refresh
    
    return () => {
      window.removeEventListener('clear-explorer-focus', handleClearFocus)
      window.removeEventListener('file-explorer-refresh', handleRefresh)
      window.removeEventListener('file-renamed', handleFileRenamed)
      window.removeEventListener('vault-changed', handleVaultChange)

      // No need to remove file-system-changed listener or fsWatcher listener
      // as we're not adding them anymore
    }
  })
  
  onDestroy(() => {
    // Stop file system watcher
    if (fsWatcher) {
      fsWatcher.stop()
    }
    
    // Clear debounce timer
    if (refreshDebounceTimer) {
      clearTimeout(refreshDebounceTimer)
      refreshDebounceTimer = null
    }
  })
  
  // Helper function to get all nodes in tree
  function getAllNodes(nodes: FileNode[]): FileNode[] {
    const result: FileNode[] = []
    for (const node of nodes) {
      result.push(node)
      if (node.children) {
        result.push(...getAllNodes(node.children))
      }
    }
    return result
  }
  
  // Find parent of a node
  function findParentOfNode(nodes: FileNode[], targetId: string, parent: FileNode | null = null): FileNode | null {
    for (const node of nodes) {
      if (node.id === targetId) {
        return parent
      }
      if (node.children) {
        const found = findParentOfNode(node.children, targetId, node)
        if (found) return found
      }
    }
    return null
  }
  
  async function buildFileTree(skipCheck: boolean = false) {
    // Check if we should skip rebuild (during file rename)
    // But allow skipping the check when called from onMount
    if (!skipCheck && explorerState.skipNextRebuild) {
      return
    }

    // Check if we're using vault storage
    const vault = get(vaultStore)
    if (vault.isInitialized && vault.vaultHandle) {
      // Build tree from vault file structure - use cache by default on initial load
      await buildVaultFileTree(false, false) // incremental=false, useFullScan=false to prefer cache
    } else {
      // Use saved folder structure for non-vault storage
      buildLocalFileTree()
    }
  }
  
  // Track last scan time to avoid excessive scanning
  let lastScanTime = 0
  const MIN_SCAN_INTERVAL = 2000 // Minimum 2 seconds between scans
  
  const RENAME_PROTECTION_WINDOW = 5000

  // Helper function to process scan results
  function processScanResults(
    nodes: any[],
    expandedFolders: Map<string, boolean>,
    previousFocusedId: string | null,
    previousSelectedFiles: Set<string>,
    previousScrollTop: number
  ) {
    // Convert scan results to FileNode structure and restore expanded states
    const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'])
    const lastRenamedOldPath = explorerState.lastRenamedOldPath
    const renameGuardActive = Date.now() - explorerState.lastRenameTimestamp < RENAME_PROTECTION_WINDOW

    // Check if folders should be collapsed by default from query string
    const foldersCollapsed = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('foldersCollapsed') === 'true'
    const defaultExpanded = !foldersCollapsed

    function getExpandedState(id: string): boolean {
      if (expandedFolders.has(id)) {
        return expandedFolders.get(id) ?? defaultExpanded
      }
      if (id.startsWith('folder:')) {
        const withoutPrefix = id.slice(7)
        return expandedFolders.get(withoutPrefix) ?? defaultExpanded
      }
      const withPrefix = `folder:${id}`
      return expandedFolders.get(withPrefix) ?? defaultExpanded
    }

    const seenNodeIds = new Set<string>()

    function convertAndRestoreState(scanNodes: any[]): FileNode[] {
      const converted: FileNode[] = []

      for (const node of scanNodes) {
        const nodeId = node.id as string | undefined

        if (renameGuardActive && lastRenamedOldPath && nodeId) {
          const normalizedNodeId = nodeId.replace(/\.md$/i, '')
          if (normalizedNodeId === lastRenamedOldPath) {
            // Skip stale entries referencing the old path during rename guard window
            continue
          }
        }

        if (node.type === 'folder') {
          if (seenNodeIds.has(node.id)) {
            continue
          }
          seenNodeIds.add(node.id)

          const children = node.children ? convertAndRestoreState(node.children) : []
          converted.push({
            id: node.id,
            name: node.name,
            type: 'folder',
            expanded: getExpandedState(node.id),
            children
          })
          continue
        }

        if (node.type !== 'file') {
          continue
        }

        const rawExtension = (node.extension || node.id?.split('.').pop() || '').toLowerCase()
        const isMarkdown = rawExtension === 'md'
        const isImage = IMAGE_EXTENSIONS.has(rawExtension)

        if (!isMarkdown && !isImage) {
          continue
        }

        const timestamp = new Date(node.modified || Date.now())
        const baseName = (node.name || node.id || '').replace(/\.[^.]+$/, '')

        if (isImage) {
          if (seenNodeIds.has(node.id)) {
            continue
          }
          seenNodeIds.add(node.id)

          const imageDoc: DocumentInfo = {
            id: node.id,
            title: baseName,
            preview: '',
            createdAt: timestamp,
            updatedAt: timestamp,
            wordCount: 0,
            isImage: true
          } as DocumentInfo

          converted.push({
            id: node.id,
            name: baseName,
            type: 'file',
            document: imageDoc,
            isImage: true,
            extension: rawExtension.toUpperCase()
          })
          continue
        }

        const normalizedId = node.id.replace(/\.md$/i, '')
        const existingDoc = documentList.find(d => d.id === normalizedId) || documentList.find(d => d.id === node.id)
        const hasTab = fileStoreState?.tabs instanceof Map
          ? (fileStoreState.tabs.has(normalizedId) ||
             fileStoreState.tabs.has(node.id) ||
             fileStoreState.tabs.has(`${normalizedId}.md`))
          : false

        const isCurrentRenameTarget = renameGuardActive && explorerState.lastRenamedNewPath === normalizedId

        if (!existingDoc && !hasTab && !isCurrentRenameTarget) {
          continue
        }

        if (seenNodeIds.has(normalizedId)) {
          continue
        }
        seenNodeIds.add(normalizedId)

        const markdownDoc: DocumentInfo = existingDoc
          ? { ...existingDoc, id: normalizedId }
          : {
              id: normalizedId,
              title: baseName,
              preview: '',
              createdAt: timestamp,
              updatedAt: timestamp,
              wordCount: 0
            } as DocumentInfo

        converted.push({
          id: normalizedId,
          name: baseName,
          type: 'file',
          document: markdownDoc,
          isReadError: markdownDoc.isReadError,
          extension: 'MD'
        })
      }

      return sortTree(converted)
    }

    // CRITICAL: Check if we're processing a rename OR if rename happened recently
    // This prevents processScanResults from overwriting the manually updated tree
    // Check both the flag and timestamp (within last 2 seconds)
    const timeSinceRename = Date.now() - explorerState.lastRenameTimestamp
    if (explorerState.isProcessingRename || timeSinceRename < 2000) {
      return
    }

    fileTree = convertAndRestoreState(nodes)

    // Restore focused node and selection
    focusedNodeId = previousFocusedId
    selectedFiles = previousSelectedFiles

    // Save folder structure after building
    saveFolderStructure()
    
    // Restore scroll position
    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('.file-explorer-content')
      if (scrollContainer && previousScrollTop) {
        scrollContainer.scrollTop = previousScrollTop
      }
    })

    if (!renameGuardActive) {
      explorerState.lastRenamedOldPath = null
      explorerState.lastRenamedNewPath = null
    }
  }
  
  async function buildVaultFileTree(incrementalUpdate = false, useFullScan = true, ignoreThrottle = false) {
    // Prevent excessive scanning
    const now = Date.now()
    if (!ignoreThrottle && now - lastScanTime < MIN_SCAN_INTERVAL && !incrementalUpdate) {
      return
    }
    lastScanTime = now

    // Save current folder expansion states and focused node before rebuilding
    const expandedFolders = new Map<string, boolean>()
    const previousFocusedId = focusedNodeId
    const previousSelectedFiles = new Set(selectedFiles)
    const previousScrollTop = document.querySelector('.file-explorer-content')?.scrollTop || 0

    function saveExpandedStates(nodes: FileNode[]) {
      for (const node of nodes) {
        if (node.type === 'folder') {
          // Save with the actual ID (which includes folder: prefix)
          expandedFolders.set(node.id, node.expanded || false)
          if (node.children) {
            saveExpandedStates(node.children)
          }
        }
      }
    }

    // Save current state if tree exists
    if (fileTree.length > 0) {
      saveExpandedStates(fileTree)
    }

    // Check if we're in demo mode
    const vault = get(vaultStore)
    const isDemoMode = vault?.kind === 'demo' || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === 'true')

    // Check if folders should be collapsed by default
    const foldersCollapsed = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('foldersCollapsed') === 'true'
    const defaultExpanded = !foldersCollapsed

    if (isDemoMode) {
      // In demo mode, build tree from fileStore documents
      // Don't call loadDocuments here - caller should have already loaded documents
      const state = get(fileStore)
      const tree: FileNode[] = []
      const folderMap = new Map<string, FileNode>()

      // Get all documents and open tabs
      const allFiles = new Set<string>()

      // Add documents from fileStore
      state.documents.forEach(doc => {
        allFiles.add(doc.id)
      })

      // Add open tabs (they might not be in documents yet)
      state.tabs.forEach(tab => {
        allFiles.add(tab.id)
      })

      // In demo mode, add template images to file list
      // Images are served from R2 CDN, so we just list them without importing
      for (const imagePath of DEMO_TEMPLATE_IMAGE_PATHS) {
        allFiles.add(imagePath)
      }

      // Process each file
      for (const fileId of allFiles) {
        const parts = fileId.split('/')

        // Create folders if file has path
        if (parts.length > 1) {
          let currentPath = ''
          for (let i = 0; i < parts.length - 1; i++) {
            currentPath = i === 0 ? parts[i] : currentPath + '/' + parts[i]
            const folderId = `folder:${currentPath}`

            if (!folderMap.has(currentPath)) {
              const folder: FileNode = {
                id: folderId,
                name: parts[i],
                type: 'folder',
                expanded: expandedFolders.get(folderId) ?? defaultExpanded,
                children: []
              }
              folderMap.set(currentPath, folder)
            }
          }
        }

        // Create file node
        const fullFileName = parts[parts.length - 1]
        const isImage = /\.(png|jpg|jpeg|gif|svg|webp|bmp)$/i.test(fullFileName)

        // Remove extension from display name for ALL file types
        // Extension will be shown separately in the file-extension label
        const displayName = fullFileName.replace(/\.[^.]+$/, '')
        const fileExtension = fullFileName.split('.').pop()

        const doc = state.documents.find(d => d.id === fileId)
        const tab = state.tabs.get(fileId)
        const fileNode: FileNode = {
          id: fileId,
          name: isImage ? displayName : (tab?.title || doc?.title || displayName),
          type: 'file',
          isImage,
          extension: fileExtension,
          document: doc || {
            id: fileId,
            title: isImage ? displayName : (tab?.title || displayName),
            preview: '',
            createdAt: new Date(),
            updatedAt: new Date(),
            wordCount: 0,
            tags: []
          }
        }

        // Add file to appropriate folder or root
        if (parts.length > 1) {
          const parentPath = parts.slice(0, -1).join('/')
          const parentFolder = folderMap.get(parentPath)
          if (parentFolder && parentFolder.children) {
            parentFolder.children.push(fileNode)
          }
        } else {
          tree.push(fileNode)
        }
      }

      // Add folders to tree (build hierarchy)
      for (const [path, folder] of folderMap) {
        const parts = path.split('/')
        if (parts.length === 1) {
          // Root folder
          tree.push(folder)
        } else {
          // Find parent folder
          const parentPath = parts.slice(0, -1).join('/')
          const parentFolder = folderMap.get(parentPath)
          if (parentFolder && parentFolder.children) {
            parentFolder.children.push(folder)
          }
        }
      }

      // Sort tree
      function sortTree(nodes: FileNode[]) {
        nodes.sort((a, b) => {
          // Folders first
          if (a.type !== b.type) {
            return a.type === 'folder' ? -1 : 1
          }
          // Then alphabetically
          return a.name.localeCompare(b.name)
        })
        // Sort children recursively
        nodes.forEach(node => {
          if (node.children) {
            sortTree(node.children)
          }
        })
      }

      sortTree(tree)

      // CRITICAL: Check if we're processing a rename before updating fileTree
      // This prevents buildVaultFileTree from overwriting the manually updated tree
      if (explorerState.isProcessingRename) {
        return
      }

      fileTree = tree

      // Restore focused node and selected files
      if (previousFocusedId && tree.some(n => n.id === previousFocusedId)) {
        focusedNodeId = previousFocusedId
      }

      previousSelectedFiles.forEach(fileId => {
        if (tree.some(n => n.id === fileId)) {
          selectedFiles.add(fileId)
        }
      })

      // Restore scroll position
      if (previousScrollTop) {
        requestAnimationFrame(() => {
          const container = document.querySelector('.file-explorer-content')
          if (container) {
            container.scrollTop = previousScrollTop
          }
        })
      }

      return
    }
    
    // If incremental update, try to use cache first
    if (incrementalUpdate && vaultStore.getCachedTree) {
      try {
        const cachedNodes = await vaultStore.getCachedTree()
        if (cachedNodes && cachedNodes.length > 0) {
          // Process cached data without full scan
          processScanResults(cachedNodes, expandedFolders, previousFocusedId, previousSelectedFiles, previousScrollTop)
          return
        }
      } catch (error) {
      }
    }
    
    // Try full metadata scan with cache first (Obsidian-style)
    if (useFullScan && vaultStore.fullMetadataScanWithCache) {
      // Completely silent scan
        // console.log('🚀 Using cached metadata for instant loading')
      isScanning = true
      scanProgress = { current: 0, total: 100, currentPath: 'Loading from cache...' }
      
      const nodes = await vaultStore.fullMetadataScanWithCache()
      
      isScanning = false
      scanProgress = null
      
      if (nodes && nodes.length > 0) {
        console.log('[FileExplorer] fullMetadataScanWithCache returned nodes', nodes.length)
        processScanResults(nodes, expandedFolders, previousFocusedId, previousSelectedFiles, previousScrollTop)
        return
      }
    }
    
    // Fallback to original list-based approach if full scan unavailable
    // console.log('Falling back to list-based file tree building')
    const files = await vaultStore.listFiles('', true) // recursive: true for file explorer
    const tree: FileNode[] = []
    const folderMap = new Map<string, FileNode>()
    
    // Sort folders by depth to ensure parents are created before children
    const folders = files.filter(f => f.type === 'directory')
      .sort((a, b) => {
        const depthA = a.path.split('/').length
        const depthB = b.path.split('/').length
        return depthA - depthB
      })
    
    // Create all folders
    for (const file of folders) {
      const folderId = `folder:${file.path}` // Prefix to ensure uniqueness
      const folder: FileNode = {
        id: folderId,
        name: file.name,
        type: 'folder',
        expanded: expandedFolders.get(folderId) ?? expandedFolders.get(file.path) ?? false, // Check both old and new format
        children: []
      }
      folderMap.set(file.path, folder) // Keep path as key for hierarchy building
    }
    
    // Build folder hierarchy
    for (const [path, folder] of folderMap) {
      const parts = path.split('/')
      if (parts.length === 1) {
        // Root folder
        tree.push(folder)
      } else {
        // Nested folder - find parent
        const parentPath = parts.slice(0, -1).join('/')
        const parent = folderMap.get(parentPath)
        if (parent && parent.children) {
          parent.children.push(folder)
        } else {
          // If parent not found, add to root (shouldn't happen with sorted folders)
          // Silently add to root if parent not found
          tree.push(folder)
        }
      }
    }
    
    // Group files by parent path to check for duplicates
    const filesByParent = new Map<string, { doc: DocumentInfo, path: string }[]>()
    
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp']
    
    for (const file of files) {
      if (file.type === 'file') {
        const isImage = imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
        
        if (file.name.endsWith('.md')) {
          const docId = file.path.replace('.md', '')
          
          // Try to find document by exact ID match
          let doc = documentList.find(d => {
            // Exact match
            if (d.id === docId) return true
            
            // Try matching if doc ID ends with the file path (for vault path issues)
            // But make sure it's actually the same path structure
            if (d.id.endsWith('/' + docId)) {
              // Verify the path structure matches
              const dParts = d.id.split('/')
              const fileParts = docId.split('/')
              if (dParts.slice(-fileParts.length).join('/') === docId) {
                return true
              }
            }
            
            return false
          })
          
          // If document not found in cache, create a minimal representation
          // Don't update store cache - let it be loaded when actually opened
          if (!doc) {
            doc = {
              id: docId,
              title: file.name.replace('.md', ''),
              preview: '',
              createdAt: new Date(),
              updatedAt: new Date(),
              wordCount: 0
            } as DocumentInfo
          }
          
          if (doc) {
            const parts = file.path.split('/')
            const parentPath = parts.length > 1 ? parts.slice(0, -1).join('/') : ''
            
            if (!filesByParent.has(parentPath)) {
              filesByParent.set(parentPath, [])
            }
            filesByParent.get(parentPath)!.push({ doc, path: file.path })
          }
        } else if (isImage) {
          // Handle image files
          const parts = file.path.split('/')
          const parentPath = parts.length > 1 ? parts.slice(0, -1).join('/') : ''
          
          if (!filesByParent.has(parentPath)) {
            filesByParent.set(parentPath, [])
          }
          
          // Create a pseudo-document for image files
          const imageDoc: DocumentInfo = {
            id: file.path,
            title: file.name,
            content: '',
            createdAt: new Date(),
            updatedAt: new Date(),
            isImage: true
          } as DocumentInfo
          
          filesByParent.get(parentPath)!.push({ doc: imageDoc, path: file.path })
        }
      }
    }
    
    // Add files to tree, handling duplicates only when necessary
    for (const [parentPath, filesInParent] of filesByParent) {
      // Count duplicates by title
      const titleCounts = new Map<string, number>()
      filesInParent.forEach(({ doc }) => {
        const count = titleCounts.get(doc.title) || 0
        titleCounts.set(doc.title, count + 1)
      })
      
      // Track how many times we've seen each title
      const titlesSeen = new Map<string, number>()
      
      for (const { doc, path } of filesInParent) {
        let displayName = doc.title

        // Remove extension from display name for ALL file types
        if (displayName) {
          displayName = displayName.replace(/\.[^.]+$/, '')
        }

        // Only add numbering if there are actual duplicates
        if (titleCounts.get(doc.title)! > 1) {
          const seenCount = titlesSeen.get(doc.title) || 0
          if (seenCount > 0) {
            // If title already ends with a number, append with hyphen
            // Otherwise append with space and parentheses
            if (/\d$/.test(doc.title)) {
              displayName = `${doc.title}-${seenCount + 1}`
            } else {
              displayName = `${doc.title} (${seenCount + 1})`
            }
          }
          titlesSeen.set(doc.title, seenCount + 1)
        }

        // Extract extension from original path
        const fileExtension = path.split('.').pop()

        const fileNode: FileNode = {
          id: path.replace('.md', ''),  // Use full path as ID, not doc.id
          name: displayName,
          type: 'file',
          document: doc,
          isReadError: doc.isReadError,
          isImage: doc.isImage,
          extension: fileExtension?.toUpperCase()
        }
        
        
        // Find parent folder and add file
        if (parentPath) {
          const parent = folderMap.get(parentPath)
          if (parent && parent.children) {
            parent.children.push(fileNode)
          } else {
            // Parent folder not found, add to root
            console.warn(`Parent folder not found for file: ${path}, adding to root`)
            tree.push(fileNode)
          }
        } else {
          // Root file
          tree.push(fileNode)
        }
      }
    }
    
    // Sort the tree before updating
    fileTree = sortTree(tree)
    
    // Restore states immediately (no animation frame needed)
    if (previousFocusedId) {
      focusedNodeId = previousFocusedId
    }
    if (previousSelectedFiles.size > 0) {
      selectedFiles = previousSelectedFiles
    }
    
    // Restore scroll position
    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('.file-explorer-content')
      if (scrollContainer && previousScrollTop) {
        scrollContainer.scrollTop = previousScrollTop
      }
    })
  }
  
  function buildLocalFileTree() {
    // Load saved folder structure
    const savedStructure = loadFolderStructure()
    
    // Recursively reconstruct nodes
    function reconstructNode(saved: any): FileNode | null {
      if (saved.type === 'folder') {
        const children = saved.children?.map(reconstructNode).filter(Boolean) || []
        return {
          id: saved.id,
          name: saved.name,
          type: 'folder',
          expanded: saved.expanded || false,
          children
        }
      } else if (saved.type === 'file') {
        // Check if document still exists
        const doc = documentList.find(d => d.id === saved.id)
        if (doc) {
          return {
            id: doc.id,
            name: doc.title,
            type: 'file',
            document: doc,
            isReadError: doc.isReadError
          }
        }
      }
      return null
    }
    
    // If we have saved structure, reconstruct it
    if (savedStructure.length > 0) {
      const reconstructedTree = savedStructure.map(reconstructNode).filter(Boolean) as FileNode[]
      
      // Collect all file IDs that are in the tree
      const filesInTree = new Set<string>()
      function collectFileIds(nodes: FileNode[]) {
        nodes.forEach(node => {
          if (node.type === 'file') {
            filesInTree.add(node.id)
          } else if (node.children) {
            collectFileIds(node.children)
          }
        })
      }
      collectFileIds(reconstructedTree)
      
      // Add documents not in tree to root
      const rootFiles = documentList
        .filter(doc => !filesInTree.has(doc.id))
        .map(doc => ({
          id: doc.id,
          name: doc.title,
          type: 'file' as const,
          document: doc,
          isReadError: doc.isReadError
        }))
      
      fileTree = [...reconstructedTree, ...rootFiles]
    } else {
      // No saved structure, just show all documents at root
      fileTree = documentList.map(doc => ({
        id: doc.id,
        name: doc.title,
        type: 'file' as const,
        document: doc,
        isReadError: doc.isReadError
      }))
    }
  }
  
  // Watch for new documents being added
  let previousDocCount = 0

  // Separate derived state to track ONLY length changes, not content changes
  const docCount = $derived(documentList.length)

  $effect(() => {
    if (docCount !== previousDocCount) {
      previousDocCount = docCount

      // Rebuild tree when document count changes, unless we're manually handling it
      // Don't debounce - rebuild immediately to avoid showing empty state
      // Use docCount instead of fileTree.length to avoid subscribing to fileTree
      // Read explorerState flags directly for reactivity
      // IMPORTANT: Also check isProcessingRename to avoid rebuilding during rename
      if (docCount > 0 && !explorerState.skipNextRebuild && !explorerState.isProcessingRename) {
        queueMicrotask(() => {
          buildFileTree() // This is async but we don't await in effect
        })
        // DON'T reset skipNextRebuild here - let handleFileRenamed manage it
      }
      // If skipNextRebuild or isProcessingRename was true, keep them until handleFileRenamed resets
    }
  })
  
  
  function toggleFolder(node: FileNode) {
    // Update the folder's expanded state in the tree
    function updateFolder(tree: FileNode[]): FileNode[] {
      return tree.map(n => {
        if (n.id === node.id && n.type === 'folder') {
          return { ...n, expanded: !n.expanded }
        } else if (n.type === 'folder' && n.children) {
          return { ...n, children: updateFolder(n.children) }
        }
        return n
      })
    }
    
    fileTree = updateFolder(fileTree)
    
    // Update focused node
    focusedNodeId = node.id
    
    // Ensure the file explorer is focused for keyboard navigation
    if (explorerEl) {
      explorerEl.focus()
    }
    
    // Save folder structure when expanding/collapsing
    saveFolderStructure()
  }
  
  async function selectFile(node: FileNode, event?: MouseEvent) {
    // Update focused node
    focusedNodeId = node.id
    
    // Ensure the file explorer is focused for keyboard navigation
    if (explorerEl) {
      explorerEl.focus()
    }
    
    // Handle multi-selection for both files and folders
    if (event) {
      const isCmdOrCtrl = event.metaKey || event.ctrlKey
      const isShift = event.shiftKey
      
      if (isCmdOrCtrl) {
        // Toggle selection for both files and folders
        const newSelection = new Set(selectedFiles) // Create new set BEFORE clearing
        if (newSelection.has(node.id)) {
          newSelection.delete(node.id)
        } else {
          newSelection.add(node.id)
        }
        selectedFiles = newSelection
        lastSelectedFile = node.id
      } else if (isShift && lastSelectedFile) {
        // Range selection for both files and folders
        const allNodeIds = getAllNodeIds(fileTree)
        const startIdx = allNodeIds.indexOf(lastSelectedFile)
        const endIdx = allNodeIds.indexOf(node.id)
        
        if (startIdx !== -1 && endIdx !== -1) {
          const newSelection = new Set<string>()
          const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx]
          for (let i = from; i <= to; i++) {
            newSelection.add(allNodeIds[i])
          }
          // Remove child nodes if their parent folders are selected
          selectedFiles = removeChildNodesFromSelection(newSelection)
        }
      } else {
        // Single selection
        selectedFiles = new Set([node.id])
        lastSelectedFile = node.id
        
        // Open file or toggle folder
        if (node.type === 'file') {
          if (node.isImage) {
            // Open image viewer
            await fileStore.openImageFile(node.id)
          } else {
            // Ensure we use the correct ID format for opening files
            // Always use relative path - storage layer will handle resolution
            await fileStore.openFile(node.id)
          }

          // Close explorer on mobile after opening file
          if (typeof window !== 'undefined' && window.innerWidth < 768) {
            updateExplorerSettings({ isOpen: false })
          }
        } else if (node.type === 'folder') {
          // Toggle folder expansion on single click without modifiers
          toggleFolder(node)
        }
      }
    } else {
      // No event - open file or toggle folder
      if (node.type === 'file') {
        if (node.isImage) {
          await fileStore.openImageFile(node.id)
        } else {
          // Always use relative path - storage layer will handle resolution
          await fileStore.openFile(node.id)
        }

        // Close explorer on mobile after opening file
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
          updateExplorerSettings({ isOpen: false })
        }
      } else if (node.type === 'folder') {
        toggleFolder(node)
      }
    }
  }
  
  // Get all visible node IDs (files and folders) in order for range selection
  function getAllNodeIds(nodes: FileNode[]): string[] {
    const ids: string[] = []
    for (const node of nodes) {
      ids.push(node.id) // Add all nodes, not just files
      // Only include children if the folder is expanded (visible)
      if (node.type === 'folder' && node.expanded && node.children) {
        ids.push(...getAllNodeIds(node.children))
      }
    }
    return ids
  }
  
  async function createNewDocument() {
    // Always create in root, regardless of current focus
    // Don't pass a title so fileStore will generate a unique one
    // Pass null for folderPath to explicitly force root creation
    const id = await fileStore.createNewDocument(undefined, null)
    
    // Get the newly created document
    const doc = documentList.find(d => d.id === id)
    if (!doc) return
    
    // Normalize the ID to match what the file system will use
    // Remove vault path prefix if present
    let normalizedId = id
    const vault = get(vaultStore)
    if (vault?.vaultPath && id.startsWith(vault.vaultPath)) {
      // Remove vault path and leading slash
      normalizedId = id.substring(vault.vaultPath.length).replace(/^\//, '')
    }
    
    const newFile: FileNode = {
      id: normalizedId,  // Use normalized ID that matches file system
      name: doc.title,
      type: 'file',
      document: doc
      // No need for isOpen - this is determined dynamically by isFileOpen()
    }
    
    // Insert at sorted position in tree
    fileTree = insertNodeSorted(fileTree, newFile)
    saveFolderStructure()
    
    // Open posting prompt for new document - use the original ID for tab
    const { postingPromptStore } = await import('$lib/stores/postingPrompt')
    const { unifiedPromptStore } = await import('$lib/stores/unifiedPrompt')
    postingPromptStore.loadForTab(id)
    unifiedPromptStore.show('posting')
  }
  
  // Custom comparison function for natural sorting
  function compareNames(a: string, b: string): number {
    // Use natural sorting for all files and folders
    // localeCompare with numeric option handles "file 1" vs "file 10" correctly
    return a.localeCompare(b, undefined, { 
      numeric: true, 
      sensitivity: 'base' 
    })
  }

  // Helper function to sort a tree recursively
  function sortTree(nodes: FileNode[]): FileNode[] {
    return nodes.sort((a, b) => {
      // Folders come first, then files
      if (a.type === 'folder' && b.type === 'file') return -1
      if (a.type === 'file' && b.type === 'folder') return 1
      
      // Within same type, use natural sorting
      return compareNames(a.name, b.name)
    }).map(node => {
      // Recursively sort children if it's a folder
      if (node.type === 'folder' && node.children) {
        return {
          ...node,
          children: sortTree(node.children)
        }
      }
      return node
    })
  }

  // Helper function to insert node in sorted position
  function insertNodeSorted(nodes: FileNode[], newNode: FileNode): FileNode[] {
    const result = [...nodes]
    
    // Find the correct position to insert
    let insertIndex = result.length
    for (let i = 0; i < result.length; i++) {
      const node = result[i]
      // Folders come first, then files
      if (newNode.type === 'folder' && node.type === 'file') {
        insertIndex = i
        break
      }
      // Within same type, use natural sorting
      if (newNode.type === node.type) {
        const comparison = compareNames(newNode.name, node.name)
        if (comparison < 0) {
          insertIndex = i
          break
        }
      }
    }
    
    result.splice(insertIndex, 0, newNode)
    return result
  }
  
  async function createNewFileInRoot() {
    // Explicitly create new file in root
    
    // Pass null to explicitly request root folder
    const id = await fileStore.createNewDocument(undefined, null)
    
    // Get the newly created document
    const doc = documentList.find(d => d.id === id)
    if (!doc) return
    
    const newFile: FileNode = {
      id,
      name: doc.title,
      type: 'file',
      document: doc
    }
    
    // Insert at sorted position in tree
    fileTree = insertNodeSorted(fileTree, newFile)
    saveFolderStructure()
    
    // Open posting prompt for new document
    const { postingPromptStore } = await import('$lib/stores/postingPrompt')
    const { unifiedPromptStore } = await import('$lib/stores/unifiedPrompt')
    postingPromptStore.loadForTab(id)
    unifiedPromptStore.show('posting')
  }
  
  async function createNewFolderInRoot() {
    // Explicitly create new folder in root
    const folderName = await promptForFolderName()
    if (!folderName) return
    
    const newFolder: FileNode = {
      id: `folder:${folderName}`,
      name: folderName,
      type: 'folder',
      expanded: true,
      children: []
    }
    
    // Insert at sorted position in tree
    fileTree = insertNodeSorted(fileTree, newFolder)
    saveFolderStructure()
  }
  
  async function createNewFile(parentFolder?: FileNode) {
    // Create new file in specific folder or root
    // Don't pass a title so fileStore will generate a unique one

    if (parentFolder && parentFolder.type === 'folder') {
      // Skip the automatic rebuild since we'll handle it manually
      explorerState.skipNextRebuild = true
    }
    
    // Pass the folder path to createNewDocument
    // Remove 'folder:' prefix from folder ID if present
    let folderPath: string | undefined = undefined
    if (parentFolder?.type === 'folder') {
      folderPath = parentFolder.id.startsWith('folder:') 
        ? parentFolder.id.slice(7)  // Remove 'folder:' prefix
        : parentFolder.id
    }
    const id = await fileStore.createNewDocument(undefined, folderPath)
    
    // Get the newly created document
    const doc = documentList.find(d => d.id === id)
    if (!doc) return
    
    const newFile: FileNode = {
      id,
      name: doc.title,
      type: 'file',
      document: doc
      // No need for isOpen - this is determined dynamically by isFileOpen()
    }
    
    if (parentFolder && parentFolder.type === 'folder') {
      // Ensure folder is expanded and add file to it at sorted position
      function updateTree(tree: FileNode[]): FileNode[] {
        return tree.map(n => {
          if (n.id === parentFolder.id && n.type === 'folder') {
            return { 
              ...n, 
              expanded: true,
              children: insertNodeSorted(n.children || [], newFile)
            }
          } else if (n.type === 'folder' && n.children) {
            return { ...n, children: updateTree(n.children) }
          }
          return n
        })
      }
      
      fileTree = updateTree(fileTree)
    } else {
      // Add to root at sorted position
      fileTree = insertNodeSorted(fileTree, newFile)
    }
    
    // Save folder structure to persist the association
    saveFolderStructure()
    
    // Open posting prompt for new document
    const { postingPromptStore } = await import('$lib/stores/postingPrompt')
    const { unifiedPromptStore } = await import('$lib/stores/unifiedPrompt')
    postingPromptStore.loadForTab(id)
    unifiedPromptStore.show('posting')
  }
  
  export async function createNewFolder(parentFolder?: FileNode) {
    // Get existing folder IDs to prevent duplicates
    const existingFolderIds = new Set<string>()
    const existingFolderNames = new Set<string>()
    
    function collectFolderIds(nodes: FileNode[]) {
      for (const node of nodes) {
        if (node.type === 'folder') {
          existingFolderIds.add(node.id)
          existingFolderNames.add(node.name.toLowerCase())
          if (node.children) {
            collectFolderIds(node.children)
          }
        }
      }
    }
    
    // Collect from appropriate scope
    if (parentFolder?.children) {
      parentFolder.children.forEach(n => {
        if (n.type === 'folder') {
          existingFolderNames.add(n.name.toLowerCase())
        }
      })
    } else {
      fileTree.forEach(n => {
        if (n.type === 'folder') {
          existingFolderNames.add(n.name.toLowerCase())
        }
      })
    }
    
    // Also collect all IDs to prevent duplicates globally
    collectFolderIds(fileTree)
    
    let folderName = 'New Folder'
    let counter = 2
    
    while (existingFolderNames.has(folderName.toLowerCase())) {
      folderName = `New Folder ${counter}`
      counter++
    }
    
    // Build the actual folder path
    let folderPath = folderName
    if (parentFolder && parentFolder.type === 'folder') {
      // Extract actual path from parent folder ID (remove 'folder:' prefix)
      const parentPath = parentFolder.id.startsWith('folder:') 
        ? parentFolder.id.slice(7) 
        : parentFolder.id
      folderPath = `${parentPath}/${folderName}`
    }
    
    try {
      explorerState.skipNextRebuild = true
      // Create the actual folder in the file system
      const { vaultStore } = await import('$lib/stores/vaultStore')
      await vaultStore.createFolder(folderPath)
      
      // Make sure folder ID is unique by prefixing with 'folder:'
      const newFolderId = `folder:${folderPath}`
      
      // Check if this ID already exists (should not happen with proper unique name generation)
      if (existingFolderIds.has(newFolderId)) {
        console.error('Duplicate folder ID detected:', newFolderId)
        const { errorService } = await import('$lib/services/ErrorService')
        errorService.showError('폴더 생성 실패: 중복된 폴더가 감지되었습니다.')
        return
      }
      
      const newFolder: FileNode = {
        id: newFolderId,
        name: folderName,
        type: 'folder',
        expanded: false,  // Start collapsed to avoid immediate rename
        children: []
      }
      
      if (parentFolder && parentFolder.type === 'folder') {
        // Ensure parent folder is expanded
        function expandParentFolder(tree: FileNode[]): FileNode[] {
          return tree.map(n => {
            if (n.id === parentFolder.id && n.type === 'folder') {
              return { ...n, expanded: true }
            } else if (n.type === 'folder' && n.children) {
              return { ...n, children: expandParentFolder(n.children) }
            }
            return n
          })
        }
        
        fileTree = expandParentFolder(fileTree)
        
        // Add to parent folder at sorted position
        function addToFolder(tree: FileNode[]): FileNode[] {
          return tree.map(n => {
            if (n.id === parentFolder.id && n.type === 'folder') {
              return { ...n, children: insertNodeSorted(n.children || [], newFolder) }
            } else if (n.type === 'folder' && n.children) {
              return { ...n, children: addToFolder(n.children) }
            }
            return n
          })
        }
        
        fileTree = addToFolder(fileTree)
      } else {
        // Add to root at sorted position
        fileTree = insertNodeSorted(fileTree, newFolder)
      }
      
      // Save folder structure
      saveFolderStructure()
      
      // Start editing the folder name immediately after creation
      startEditing(newFolder)
      
      // Ensure the new folder is visible and focused
      setTimeout(() => {
        // Find the input element for the new folder
        const input = document.querySelector(`[data-folder-id="${newFolder.id}"] .edit-input`) as HTMLInputElement
        if (input) {
          // Scroll the input into view
          input.scrollIntoView({ behavior: 'smooth', block: 'center' })
          // Focus and select all text
          input.focus()
          input.select()
        }
      }, 100)
      
    } catch (err) {
      console.error('Failed to create folder:', err)
      // Show error to user
      const { errorService } = await import('$lib/services/ErrorService')
      errorService.showError('폴더 생성에 실패했습니다: ' + (err instanceof Error ? err.message : '알 수 없는 오류'))
    }
  }
  
  // Folder expansion state
  let folderExpansionLevel = $state<'all-expanded' | 'all-collapsed' | 'mixed'>('mixed')
  
  function toggleAllFolders() {
    // Determine next state based on current state
    if (folderExpansionLevel === 'all-expanded') {
      // Collapse all
      collapseAllFolders()
      folderExpansionLevel = 'all-collapsed'
    } else if (folderExpansionLevel === 'all-collapsed') {
      // Expand all
      expandAllFolders()
      folderExpansionLevel = 'all-expanded'
    } else {
      // Mixed state - expand all first
      expandAllFolders()
      folderExpansionLevel = 'all-expanded'
    }
  }
  
  function expandAllFolders() {
    // Recursively expand all folders including subfolders
    function expandNode(node: FileNode): FileNode {
      if (node.type === 'folder') {
        return {
          ...node,
          expanded: true,
          children: node.children?.map(expandNode)
        }
      }
      return node
    }
    
    fileTree = fileTree.map(expandNode)
  }
  
  function collapseAllFolders() {
    // Recursively collapse all folders including subfolders
    function collapseNode(node: FileNode): FileNode {
      if (node.type === 'folder') {
        return {
          ...node,
          expanded: false,
          children: node.children?.map(collapseNode)
        }
      }
      return node
    }
    
    fileTree = fileTree.map(collapseNode)
  }
  
  // Check if there are any folders
  let hasFolders = $derived(fileTree.some(n => n.type === 'folder'))
  
  // Update folder expansion state when folders change
  $effect(() => {
    if (fileTree.length > 0) {
      const folders = fileTree.filter(n => n.type === 'folder')
      if (folders.length === 0) {
        folderExpansionLevel = 'mixed'
      } else {
        const expandedCount = folders.filter(f => f.expanded).length
        if (expandedCount === 0) {
          folderExpansionLevel = 'all-collapsed'
        } else if (expandedCount === folders.length) {
          folderExpansionLevel = 'all-expanded'
        } else {
          folderExpansionLevel = 'mixed'
        }
      }
    }
  })
  
  async function deleteSelectedNodes() {
    // If no files are selected, delete the focused node
    if (selectedFiles.size === 0 && focusedNodeId) {
      const node = findNodeById(fileTree, focusedNodeId)
      if (node) {
        await deleteNode(node)
      }
      return
    }
    
    // If files are selected, delete all selected files
    if (selectedFiles.size > 0) {
      // Create a copy of selected files before any operations
      const selectedFilesCopy = new Set(selectedFiles)
      
      // Count files and folders separately
      let fileCount = 0
      let folderCount = 0
      for (const nodeId of selectedFilesCopy) {
        const node = findNodeById(fileTree, nodeId)
        if (node) {
          if (node.type === 'file') {
            fileCount++
          } else if (node.type === 'folder') {
            folderCount++
          }
        }
      }
      
      // Check if in demo mode
      const vault = get(vaultStore)
      const isDemoMode = vault?.kind === 'demo' || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('demo') === 'true')

      if (isDemoMode) {
        toast.warning('데모 모드에서는 파일을 삭제할 수 없습니다. 새로고침하면 모든 변경사항이 초기화됩니다.')
        return
      }

      // Create appropriate message
      let message = ''
      if (folderCount > 0 && fileCount > 0) {
        message = `선택한 ${folderCount}개의 폴더와 ${fileCount}개의 파일을 삭제하시겠습니까?`
      } else if (folderCount > 0) {
        message = `선택한 ${folderCount}개의 폴더를 삭제하시겠습니까?`
      } else if (fileCount > 0) {
        message = `선택한 ${fileCount}개의 파일을 삭제하시겠습니까?`
      } else {
        return // Nothing to delete
      }

      const confirmed = await modal.confirm({
        title: '선택한 항목 삭제',
        message,
        confirmText: '삭제',
        confirmButtonClass: 'danger'
      })

      if (!confirmed) return
      
      // Collect all files to delete (including files in selected folders) and folders to remove
      const filesToDelete = new Set<string>()
      const foldersToRemove = new Set<string>()
      
      for (const nodeId of selectedFilesCopy) {
        const node = findNodeById(fileTree, nodeId)
        if (!node) continue
        
        if (node.type === 'file') {
          filesToDelete.add(node.id)
        } else if (node.type === 'folder') {
          // Mark folder for removal
          foldersToRemove.add(node.id)
          
          // Collect all files in the folder recursively
          function collectFileIds(folderNode: FileNode) {
            if (folderNode.children) {
              for (const child of folderNode.children) {
                if (child.type === 'file' && child.id) {
                  filesToDelete.add(child.id)
                } else if (child.type === 'folder') {
                  collectFileIds(child)
                }
              }
            }
          }
          collectFileIds(node)
        }
      }
      
      // Delete all collected files in parallel using Promise.all
      const deletePromises = Array.from(filesToDelete).map(async (fileId) => {
        try {
          // Check if this is an image file
          const node = findNodeById(fileTree, fileId)
          if (node?.isImage) {
            // Delete image file directly without adding .md extension
            const vault = get(vaultStore)
            if (vault.vaultPath) {
              const fullPath = `${vault.vaultPath}/${fileId}`
              await vaultStore.deleteFile(fileId)
            }
            
            // Dispatch event for image deletion
            window.dispatchEvent(new CustomEvent('image-file-deleted', {
              detail: { filePath: fileId }
            }))
            
            // Also remove from the file tree
            return { fileId, success: true }
          } else {
            // Regular markdown document deletion
            await fileStore.deleteDocument(fileId)
            return { fileId, success: true }
          }
        } catch (error) {
          console.error(`Failed to delete file ${fileId}:`, error)
          return { fileId, success: false, error }
        }
      })
      
      await Promise.all(deletePromises)
      
      // Delete all folders after files are deleted
      // Sort folders by depth (deepest first) to avoid trying to delete parent before children
      const sortedFolders = Array.from(foldersToRemove).sort((a, b) => {
        const depthA = a.split('/').length
        const depthB = b.split('/').length
        return depthB - depthA // Delete deeper folders first
      })
      
      for (const folderId of sortedFolders) {
        try {
          // Extract actual path from folder ID (remove 'folder:' prefix if present)
          const folderPath = folderId.startsWith('folder:') ? folderId.slice(7) : folderId
          await vaultStore.deleteFolder(folderPath)
        } catch (err) {
          console.error('Failed to delete folder:', folderId, err)
        }
      }
      
      // Save expanded state before refresh
      const expandedState = new Map<string, boolean>()
      function saveExpandedState(nodes: FileNode[]) {
        for (const node of nodes) {
          if (node.type === 'folder' && node.expanded) {
            expandedState.set(node.id, true)
          }
          if (node.children) {
            saveExpandedState(node.children)
          }
        }
      }
      saveExpandedState(fileTree)
      
      // Don't refresh here - let the file system watcher handle it
      // This avoids duplicate scans
      // await buildVaultFileTree()
      
      // Restore expanded state
      function restoreExpandedState(nodes: FileNode[]) {
        for (const node of nodes) {
          if (node.type === 'folder' && expandedState.has(node.id)) {
            node.expanded = true
          }
          if (node.children) {
            restoreExpandedState(node.children)
          }
        }
      }
      restoreExpandedState(fileTree)

      // Remove deleted files from the tree structure immediately
      if (filesToDelete.size > 0) {
        function removeDeletedFiles(nodes: FileNode[]): FileNode[] {
          return nodes.filter(node => {
            // Remove files that were deleted
            if (node.type === 'file' && filesToDelete.has(node.id)) {
              return false
            }
            // Recursively filter children
            if (node.children) {
              node.children = removeDeletedFiles(node.children)
            }
            return true
          })
        }

        fileTree = removeDeletedFiles(fileTree)
      }

      // Remove folders from the tree structure
      if (foldersToRemove.size > 0) {
        function removeFoldersFromTree(nodes: FileNode[]): FileNode[] {
          return nodes.filter(node => {
            if (foldersToRemove.has(node.id)) {
              return false // Remove this folder
            }
            if (node.children) {
              node.children = removeFoldersFromTree(node.children)
            }
            return true
          })
        }

        fileTree = removeFoldersFromTree(fileTree)
        saveFolderStructure()
      }

      // Clear selection after deletion
      selectedFiles.clear()
      selectedFiles = new Set()

      // Don't call buildFileTree() here - the file system watcher will handle the refresh
      // Calling buildFileTree() immediately may read stale cache/filesystem state
      // and show deleted files again before the watcher detects the deletion
    }
  }
  
  async function deleteNode(node: FileNode, event?: MouseEvent) {
    event?.stopPropagation()
    
    if (node.type === 'file') {
      const confirmed = await modal.confirm({
        title: '파일 삭제',
        message: `"${node.name}"을(를) 삭제하시겠습니까?`,
        confirmText: '삭제',
        confirmButtonClass: 'danger'
      })
      
      if (!confirmed) return
      
      await fileStore.deleteDocument(node.id)
    } else if (node.type === 'folder') {
      const confirmed = await modal.confirm({
        title: '폴더 삭제',
        message: `"${node.name}" 폴더와 모든 하위 파일을 삭제하시겠습니까?`,
        confirmText: '삭제',
        confirmButtonClass: 'danger'
      })
      
      if (!confirmed) return
      
      // Collect all file IDs and subfolder IDs in the folder recursively
      const fileIds: string[] = []
      const folderIds: string[] = []
      
      function collectIds(folder: FileNode) {
        if (folder.children) {
          for (const child of folder.children) {
            if (child.type === 'file') {
              fileIds.push(child.id)
            } else if (child.type === 'folder') {
              // Collect subfolder IDs (from deepest to shallowest)
              collectIds(child)
              folderIds.push(child.id)
            }
          }
        }
      }
      
      collectIds(node)
      
      // Delete all files first
      for (const fileId of fileIds) {
        await fileStore.deleteDocument(fileId)
        // Also remove from selection if selected
        if (selectedFiles.has(fileId)) {
          selectedFiles.delete(fileId)
        }
      }
      
      // Delete all subfolders (from deepest to shallowest)
      const { vaultStore } = await import('$lib/stores/vaultStore')
      for (const folderId of folderIds) {
        try {
          // Extract actual path from folder ID (remove 'folder:' prefix)
          const folderPath = folderId.startsWith('folder:') ? folderId.slice(7) : folderId
          await vaultStore.deleteFolder(folderPath)
        } catch (err) {
          console.error('Failed to delete subfolder:', folderId, err)
        }
        // Remove from selection if selected
        if (selectedFiles.has(folderId)) {
          selectedFiles.delete(folderId)
        }
      }
      
      // Finally delete the main folder itself
      try {
        // Extract actual path from node ID (remove 'folder:' prefix)
        const folderPath = node.id.startsWith('folder:') ? node.id.slice(7) : node.id
        await vaultStore.deleteFolder(folderPath)
      } catch (err) {
        console.error('Failed to delete folder:', node.id, err)
      }
      
      // Remove the folder from selection if selected
      if (selectedFiles.has(node.id)) {
        selectedFiles.delete(node.id)
      }
      
      // Remove the folder from the tree
      function removeFolder(tree: FileNode[]): FileNode[] {
        return tree.reduce<FileNode[]>((acc, n) => {
          if (n.id === node.id) {
            // Skip this folder entirely
            return acc
          }
          if (n.type === 'folder' && n.children) {
            // Recursively process children
            return [...acc, { ...n, children: removeFolder(n.children) }]
          }
          return [...acc, n]
        }, [])
      }
      
      fileTree = removeFolder(fileTree)
      // Trigger reactivity
      fileTree = fileTree
      
      // Save folder structure after deletion
      saveFolderStructure()
      
      // Delay refresh to avoid immediate re-scan during deletion animation
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('file-explorer-refresh'))
        }
      }, 300)
    }
  }
  
  function startEditing(node: FileNode, event?: MouseEvent) {
    event?.stopPropagation()
    editingNodeId = node.id
    editingTitle = node.name
    editingOriginalNode = { ...node } // Store a copy of the original node
    editingDuplicateWarning = '' // Clear any previous warning
    
    // Focus on input after a short delay to ensure DOM update
    setTimeout(() => {
      // Try to find the specific input for this node
      let input: HTMLInputElement | null = null
      
      if (node.type === 'folder') {
        input = document.querySelector(`[data-folder-id="${node.id}"] .edit-input`) as HTMLInputElement
      } else {
        input = document.querySelector(`[data-file-id="${node.id}"] .edit-input`) as HTMLInputElement
      }
      
      // Fallback to any edit input if specific one not found
      if (!input) {
        input = document.querySelector('.edit-input') as HTMLInputElement
      }
      
      if (input) {
        // Ensure it's visible
        input.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        input.focus()
        // Select all text for easy replacement
        input.select()
      }
    }, 50)
  }
  
  // Helper function to clear duplicate warning with minimum duration
  function clearDuplicateWarning() {
    // Clear any existing timeout
    if (duplicateWarningTimeout) {
      clearTimeout(duplicateWarningTimeout)
      duplicateWarningTimeout = null
    }

    // If there's no warning or timestamp, just clear
    if (!editingDuplicateWarning || !duplicateWarningTimestamp) {
      editingDuplicateWarning = ''
      duplicateWarningTimestamp = null
      return
    }

    // Calculate elapsed time since warning was shown
    const elapsed = Date.now() - duplicateWarningTimestamp
    const remaining = DUPLICATE_WARNING_MIN_DURATION - elapsed

    if (remaining <= 0) {
      // Minimum duration has passed, clear immediately
      editingDuplicateWarning = ''
      duplicateWarningTimestamp = null
    } else {
      // Schedule clearing after remaining time
      duplicateWarningTimeout = setTimeout(() => {
        editingDuplicateWarning = ''
        duplicateWarningTimestamp = null
        duplicateWarningTimeout = null
      }, remaining)
    }
  }

  // Check for duplicate names in real-time
  function checkDuplicateName(newName: string) {
    if (!editingNodeId || !newName.trim()) {
      clearDuplicateWarning()
      return
    }

    const trimmedName = newName.trim()

    // Find the node being edited
    const findNode = (nodes: FileNode[]): FileNode | undefined => {
      for (const node of nodes) {
        if (node.id === editingNodeId) return node
        if (node.children) {
          const found = findNode(node.children)
          if (found) return found
        }
      }
      return undefined
    }

    const nodeBeingEdited = findNode(fileTree)
    if (!nodeBeingEdited) {
      clearDuplicateWarning()
      return
    }

    // If name hasn't changed, no warning
    if (nodeBeingEdited.name === trimmedName) {
      clearDuplicateWarning()
      return
    }

    if (nodeBeingEdited.type === 'folder') {
      // Check for duplicate folder names
      const parentPath = (() => {
        const lastSlash = nodeBeingEdited.id.lastIndexOf('/')
        if (lastSlash >= 0) {
          const idWithoutPrefix = nodeBeingEdited.id.startsWith('folder:') ? nodeBeingEdited.id.slice(7) : nodeBeingEdited.id
          return idWithoutPrefix.substring(0, lastSlash)
        }
        return ''
      })()

      // Find siblings
      const siblings: FileNode[] = []
      if (parentPath) {
        const findParentChildren = (nodes: FileNode[]): void => {
          for (const node of nodes) {
            const nodePathWithoutPrefix = node.id.startsWith('folder:') ? node.id.slice(7) : node.id
            if (nodePathWithoutPrefix === parentPath && node.type === 'folder' && node.children) {
              siblings.push(...node.children)
              return
            }
            if (node.children) {
              findParentChildren(node.children)
            }
          }
        }
        findParentChildren(fileTree)
      } else {
        siblings.push(...fileTree)
      }

      // Check for duplicates
      const hasDuplicate = siblings.some(sibling =>
        sibling.id !== nodeBeingEdited.id &&
        sibling.name.toLowerCase() === trimmedName.toLowerCase()
      )

      if (hasDuplicate) {
        // Set warning and timestamp if not already set
        if (!editingDuplicateWarning) {
          duplicateWarningTimestamp = Date.now()
        }
        editingDuplicateWarning = `"${trimmedName}"은(는) 이미 이 위치에 존재합니다.`
      } else {
        clearDuplicateWarning()
      }
    } else {
      // Check for duplicate file names
      const parentFolder = findParentOfNode(fileTree, editingNodeId)
      const siblings = parentFolder?.children || fileTree

      // Check with and without .md extension
      const newNameWithoutExt = trimmedName.replace(/\.md$/, '')

      const hasDuplicate = siblings.some(sibling =>
        sibling.id !== editingNodeId &&
        sibling.type === 'file' &&
        (sibling.name.toLowerCase() === newNameWithoutExt.toLowerCase() ||
         sibling.name.toLowerCase() === trimmedName.toLowerCase())
      )

      if (hasDuplicate) {
        // Set warning and timestamp if not already set
        if (!editingDuplicateWarning) {
          duplicateWarningTimestamp = Date.now()
        }
        editingDuplicateWarning = `"${trimmedName}"은(는) 이미 이 위치에 존재합니다.`
      } else {
        clearDuplicateWarning()
      }
    }
  }
  
  function cancelEditing() {
    // Clear any pending timeout
    if (duplicateWarningTimeout) {
      clearTimeout(duplicateWarningTimeout)
      duplicateWarningTimeout = null
    }

    editingNodeId = null
    editingTitle = ''
    editingOriginalNode = null
    editingDuplicateWarning = ''
    duplicateWarningTimestamp = null
    explorerState.skipNextRebuild = false

    // Restore focus to the file explorer for keyboard navigation
    setTimeout(() => {
      if (explorerEl) {
        explorerEl.focus()
      }
    }, 0)
  }
  
  async function finishEditing() {
    if (editingNodeId && editingTitle && editingTitle.trim() && editingOriginalNode) {
      // Prevent duplicate execution
      if (isProcessingEdit) {
        return
      }
      isProcessingEdit = true
      
      const newName = editingTitle.trim()

      // Use the stored original node for the rename operation
      const nodeToRename = editingOriginalNode
      
      // Handle folder rename
      if (nodeToRename.type === 'folder') {
        // Check if the name actually changed
        if (nodeToRename.name === newName) {
          editingNodeId = null
          editingTitle = ''
          isProcessingEdit = false
          return
        }
        
        // Check for duplicate folder names in the same parent
        const parentPath = (() => {
          const lastSlash = nodeToRename.id.lastIndexOf('/')
          if (lastSlash >= 0) {
            const idWithoutPrefix = nodeToRename.id.startsWith('folder:') ? nodeToRename.id.slice(7) : nodeToRename.id
            return idWithoutPrefix.substring(0, lastSlash)
          }
          return ''
        })()
        
        // Find siblings to check for duplicates
        const siblings: FileNode[] = []
        if (parentPath) {
          // Find parent folder and get its children
          const findParentChildren = (nodes: FileNode[]): void => {
            for (const node of nodes) {
              const nodePathWithoutPrefix = node.id.startsWith('folder:') ? node.id.slice(7) : node.id
              if (nodePathWithoutPrefix === parentPath && node.type === 'folder' && node.children) {
                siblings.push(...node.children)
                return
              }
              if (node.children) {
                findParentChildren(node.children)
              }
            }
          }
          findParentChildren(fileTree)
        } else {
          // Root level siblings
          siblings.push(...fileTree)
        }
        
        // Check if new name already exists among siblings
        const hasDuplicate = siblings.some(sibling => 
          sibling.id !== nodeToRename.id && 
          sibling.name.toLowerCase() === newName.toLowerCase()
        )
        
        if (hasDuplicate) {
          // Cancel editing and revert to original name
          editingNodeId = null
          editingTitle = ''
          editingDuplicateWarning = ''
          isProcessingEdit = false
          return
        }
        
        // Extract actual path from folder ID (remove 'folder:' prefix if present)
        const oldPath = nodeToRename.id.startsWith('folder:') ? nodeToRename.id.slice(7) : nodeToRename.id
        
        // Calculate new path
        let newPath = newName
        const lastSlash = oldPath.lastIndexOf('/')
        if (lastSlash >= 0) {
          // Folder is in a subfolder
          newPath = oldPath.substring(0, lastSlash + 1) + newName
        }
        
        // Rename the folder on the filesystem
        try {
          const { vaultStore } = await import('$lib/stores/vaultStore')
          const vault = get(vaultStore)
          const vaultPath = vault?.vaultPath || ''
          
          // Build full paths if needed
          let fullOldPath = oldPath
          let fullNewPath = newPath
          
          if (vaultPath && !oldPath.startsWith(vaultPath)) {
            fullOldPath = `${vaultPath}/${oldPath}`
          }
          if (vaultPath && !newPath.startsWith(vaultPath)) {
            fullNewPath = `${vaultPath}/${newPath}`
          }
          
          // Call the rename API
          if (window.api?.renameFolder) {
            const result = await window.api.renameFolder(fullOldPath, fullNewPath)
            if (!result || result.success === false) {
              // Failed to rename folder
              // Revert UI changes
              editingNodeId = null
              editingTitle = ''
              return
            }
          } else {
            // renameFolder API not available
            editingNodeId = null
            editingTitle = ''
            return
          }
        } catch (err) {
          // Failed to rename folder
          // Revert UI changes
          editingNodeId = null
          editingTitle = ''
          return
        }
        
        // Update all child paths recursively
        const updateChildPaths = (node: FileNode, oldPrefix: string, newPrefix: string, isRoot: boolean = false): FileNode => {
          if (node.type === 'folder') {
            // For folders, update the ID with the folder: prefix
            const currentPath = node.id.startsWith('folder:') ? node.id.slice(7) : node.id
            const newFolderPath = currentPath.replace(oldPrefix, newPrefix)
            const newId = `folder:${newFolderPath}`
            
            const updatedChildren = node.children?.map(child => 
              updateChildPaths(child, oldPrefix, newPrefix, false)
            ) || []
            // Only update the name for the root folder being renamed
            return { 
              ...node, 
              id: newId, 
              name: isRoot ? newName : node.name, 
              children: updatedChildren 
            }
          } else {
            // File IDs need to be updated too if they're under this folder
            const newId = node.id.startsWith(oldPrefix + '/') 
              ? node.id.replace(oldPrefix, newPrefix)
              : node.id
            return { ...node, id: newId }
          }
        }
        
        // Update the node tree
        const updateNodeInTree = (nodes: FileNode[]): FileNode[] => {
          return nodes.map(node => {
            if (node.id === editingNodeId) {
              return updateChildPaths(node, oldPath, newPath, true)
            } else if (node.children) {
              return { ...node, children: updateNodeInTree(node.children) }
            }
            return node
          })
        }
        
        fileTree = updateNodeInTree(fileTree)
      } else {
        // For files, check for duplicates in the same directory
        
        // Find parent folder of the file
        const parentFolder = findParentOfNode(fileTree, editingNodeId)
        const siblings = parentFolder?.children || fileTree

        // Get the node being renamed
        const nodeBeingRenamed = editingOriginalNode

        // Detect if this is an image file or markdown file
        const isImageFile = /\.(png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i.test(nodeBeingRenamed.id)

        // Check if new name already exists among siblings
        const newNameWithoutExt = newName.replace(/\.(md|png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i, '')

        const hasDuplicate = siblings.some(sibling =>
          sibling.id !== editingNodeId &&
          sibling.type === 'file' &&
          sibling.name.toLowerCase() === newNameWithoutExt.toLowerCase()
        )
        
        if (hasDuplicate) {
          // Cancel editing and revert to original name
          editingNodeId = null
          editingTitle = ''
          editingDuplicateWarning = ''
          isProcessingEdit = false
          return
        }
        
        // For files, handle rename based on whether file exists

        try {
          const displayName = newName.replace(/\.(md|png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i, '')

          // Check if file actually exists on disk
          // For images, use the path as-is. For markdown, add .md if needed
          let testPath = nodeBeingRenamed.id
          if (!isImageFile && !testPath.endsWith('.md')) {
            testPath = testPath + '.md'
          }

          const fileExistsOnDisk = await vaultStore.fileExists(testPath)

          if (!fileExistsOnDisk) {
            // File does not exist on disk yet
          }

          // Check if this is in the file store
          const fileStoreState = fileStore.getState()
          const tab = fileStoreState.tabs.get(nodeBeingRenamed.id)

          if (!fileExistsOnDisk) {
            // For image files, they should always exist on disk
            // If they don't exist, something is wrong - skip this case
            if (isImageFile) {
              console.error('[FileExplorer] Image file does not exist on disk:', testPath)
              editingNodeId = null
              editingTitle = ''
              editingDuplicateWarning = ''
              editingOriginalNode = null
              isProcessingEdit = false
              return
            }

            // File doesn't exist on disk - just update the title and save as new file (markdown only)
            
            if (tab) {
              // Update tab title which will save with the new name
              fileStore.updateTabTitle(nodeBeingRenamed.id, displayName)
              
              // Update the content's first line (title) if it starts with #
              if (tab.content) {
                const lines = tab.content.split('\n')
                if (lines[0] && lines[0].startsWith('#')) {
                  lines[0] = `# ${displayName}`
                  const updatedContent = lines.join('\n')
                  fileStore.updateTabContent(nodeBeingRenamed.id, updatedContent)
                }
              }
              
              // Save the file with the new name
              await fileStore.saveTab(nodeBeingRenamed.id)
            }
            
            // Update the tree node with new name
            const updateNodeNameForNew = (nodes: FileNode[]): FileNode[] => {
              return nodes.map(node => {
                if (node.id === editingNodeId) {
                  return { 
                    ...node,
                    name: displayName
                  }
                } else if (node.children) {
                  return { ...node, children: updateNodeNameForNew(node.children) }
                }
                return node
              })
            }
            
            fileTree = updateNodeNameForNew(fileTree)
            
            // Re-sort the tree after renaming
            fileTree = sortTree(fileTree)
            
            // Exit early - don't run the code below for new files
            editingNodeId = null
            editingTitle = ''
            editingDuplicateWarning = ''
            editingOriginalNode = null
            isProcessingEdit = false
            return
            
          } else {
            // This is an existing file - perform actual rename
            // Detect file extension from original file
            const getFileExtension = (filename: string): string => {
              const match = filename.match(/\.(md|png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i)
              return match ? match[0] : '.md'
            }

            const originalExtension = getFileExtension(nodeBeingRenamed.id)
            const isMarkdownFile = originalExtension === '.md'

            // Get the old file path (with extension)
            const oldPath = nodeBeingRenamed.id.includes('.')
              ? nodeBeingRenamed.id
              : nodeBeingRenamed.id + originalExtension

            // For new filename, preserve the original extension
            // If user included extension in newName, use it; otherwise add original extension
            const newFileName = newName.includes('.') ? newName : newName + originalExtension

            const newPath = (() => {
              const lastSlash = nodeBeingRenamed.id.lastIndexOf('/')
              if (lastSlash >= 0) {
                const folderPath = nodeBeingRenamed.id.substring(0, lastSlash)
                // Remove extension for path calculation, will add back later
                const nameWithoutExt = newFileName.replace(/\.(md|png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i, '')
                return `${folderPath}/${nameWithoutExt}`
              } else {
                return newFileName.replace(/\.(md|png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i, '')
              }
            })()

            const fullNewPath = newPath + originalExtension

            // Rename the file in the vault
            await vaultStore.renameFile(oldPath, fullNewPath)

            // If this is an image file, update all markdown files that reference it
            if (!isMarkdownFile) {
              const oldRelativePath = oldPath
              const newRelativePath = fullNewPath

              // Update all markdown files that reference this image
              if (window.api?.updateImageReferences) {
                await window.api.updateImageReferences(oldRelativePath, newRelativePath)
              }
            }

            // Update the tab if it's open (only for markdown files)
            if (isMarkdownFile) {
              if (tab) {
                // Tab is open - update in memory and save
                fileStore.updateTabPath(nodeBeingRenamed.id, newPath)
                fileStore.updateTabTitle(newPath, displayName)

                // Update content title
                const lines = tab.content.split('\n')
                if (lines[0] && lines[0].startsWith('#')) {
                  lines[0] = `# ${displayName}`
                  const updatedContent = lines.join('\n')
                  fileStore.updateTabContent(newPath, updatedContent)
                }

                await fileStore.saveTab(newPath)
              } else {
                // Tab is not open - read file, update first line, and save
                try {
                  const content = await vaultStore.readFile(fullNewPath)
                  const lines = content.split('\n')
                  if (lines[0] && lines[0].startsWith('#')) {
                    lines[0] = `# ${displayName}`
                    const updatedContent = lines.join('\n')
                    await vaultStore.writeFile(fullNewPath, updatedContent)
                  }
                } catch (err) {
                  console.warn('[FileExplorer] Failed to update title in closed file:', err)
                }
              }
            }

            // Update the document in our local list (only for markdown files)
            if (nodeBeingRenamed.document && isMarkdownFile) {
              const docIndex = documentList.findIndex(d => d.id === nodeBeingRenamed.document?.id)
              if (docIndex !== -1) {
                documentList[docIndex] = {
                  ...documentList[docIndex],
                  id: newPath,
                  title: displayName
                }
              }
            }

            // Update the tree UI with new ID and name
            const updateNodeName = (nodes: FileNode[]): FileNode[] => {
              return nodes.map(node => {
                if (node.id === editingNodeId) {
                  // For display, remove extension
                  const displayNameWithoutExt = newFileName.replace(/\.(md|png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i, '')
                  return {
                    ...node,
                    id: newPath,
                    name: displayNameWithoutExt,
                    document: node.document && isMarkdownFile ? {
                      ...node.document,
                      id: newPath,
                      title: displayNameWithoutExt
                    } : undefined
                  }
                } else if (node.children) {
                  return { ...node, children: updateNodeName(node.children) }
                }
                return node
              })
            }

            fileTree = updateNodeName(fileTree)

            // Re-sort the tree after renaming
            fileTree = sortTree(fileTree)

            // Refresh the document list to reflect the change (only for markdown files)
            if (isMarkdownFile) {
              await fileStore.loadDocuments()
            }

            // Force refresh file explorer to show updated image filename
            window.dispatchEvent(new CustomEvent('file-explorer-refresh', { detail: { force: true, allowDuringEdit: true } }))
          }
          
        } catch (err) {
          // Check if the file was actually renamed despite the error
          // This can happen if the file system operation succeeded but there's a timing issue
          try {
            // Try to verify if the new file exists
            await vaultStore.readFile(newPath + '.md')
            // If we can read the new file, the rename succeeded despite the error
            console.warn('Rename succeeded despite error:', err)
          } catch (verifyErr) {
            // The rename actually failed
            // Failed to rename file
            // Show error to user
            alert(`파일명 변경 실패: ${err.message}`)
            
            // Revert UI changes
            editingNodeId = null
            editingTitle = ''
            editingOriginalNode = null
            isProcessingEdit = false
            return
          }
        }
      }
      
      // Save folder structure after rename
      saveFolderStructure()
    }
    editingNodeId = null
    editingTitle = ''
    editingOriginalNode = null
    editingDuplicateWarning = ''
    isProcessingEdit = false
    explorerState.skipNextRebuild = false

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('file-explorer-refresh', {
        detail: { force: true, allowDuringEdit: true }
      }))
    }

    // Restore focus to the file explorer after editing is complete
    setTimeout(() => {
      if (explorerEl) {
        explorerEl.focus()
      }
    }, 0)
  }
  
  function handleEditKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      finishEditing()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEditing()
    } else if (e.key === ' ') {
      // Allow space in edit mode - don't prevent default
      e.stopPropagation() // Stop it from bubbling to parent handlers
    }
  }
  
  async function showContextMenu(node: FileNode, event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    
    // Check if multiple files are selected
    const isMultipleSelection = selectedFiles.size > 1
    const isBulkOperation = isMultipleSelection && selectedFiles.has(node.id)
    
    if (isElectron) {
      // Use Electron native context menu
      try {
        const response = await window.api.showContextMenu({
          x: event.clientX,
          y: event.clientY,
          nodeType: isBulkOperation ? 'bulk' : node.type,
          nodeId: node.id,
          nodeName: isBulkOperation ? `${selectedFiles.size}개 항목` : node.name,
          isExpanded: node.expanded,
          selectedCount: isBulkOperation ? selectedFiles.size : 1
        })
        
        // If no selection was made (menu closed), just return
        if (!response) return
        
        // Handle the response
        switch (response.action) {
          case 'rename':
            // Only allow rename for single selection
            if (!isBulkOperation) {
              startEditing(node)
            }
            break
          case 'delete':
            if (isBulkOperation) {
              // Delete all selected files
              deleteSelectedNodes()
            } else {
              // Delete single file
              deleteNode(node)
            }
            break
          case 'toggle':
            toggleFolder(node)
            break
          case 'newFile':
            await createNewFile(node)
            break
          case 'newFolder':
            await createNewFolder(node)
            break
        }
      } catch (error: any) {
        console.error('Failed to show context menu:', error)
        // Fallback to CSS menu for errors
        contextMenuNode = node
        contextMenuPosition = { x: event.clientX, y: event.clientY }
        contextMenuType = 'node'
      }
    } else {
      // Use CSS context menu for web
      contextMenuNode = node
      contextMenuPosition = { x: event.clientX, y: event.clientY }
      contextMenuType = 'node'
    }
  }
  
  async function showBlankContextMenu(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    
    // Only show if clicking on empty space
    const target = event.target as HTMLElement
    if (target.closest('.file-node') || target.closest('.folder-node')) {
      return
    }
    
    if (isElectron) {
      // Use Electron native context menu
      try {
        const response = await window.api.showContextMenu({
          x: event.clientX,
          y: event.clientY,
          nodeType: 'blank',
          nodeId: null,  // Explicitly set nodeId to null for root
          nodeName: null  // Explicitly set nodeName to null for root
        })
        
        // If no selection was made (menu closed), just return
        if (!response) return
        
        // Handle the response
        switch (response.action) {
          case 'newFile':
            await createNewFileInRoot()  // Explicitly create in root
            break
          case 'newFolder':
            await createNewFolderInRoot()  // Explicitly create in root
            break
        }
      } catch (error: any) {
        console.error('Failed to show context menu:', error)
        // Fallback to CSS menu for errors
        contextMenuNode = null
        contextMenuPosition = { x: event.clientX, y: event.clientY }
        contextMenuType = 'blank'
      }
    } else {
      // Use CSS context menu for web
      contextMenuNode = null
      contextMenuPosition = { x: event.clientX, y: event.clientY }
      contextMenuType = 'blank'
    }
  }
  
  function hideContextMenu() {
    contextMenuNode = null
    contextMenuType = 'node'
  }
  
  // Get all visible nodes in order (for keyboard navigation)
  function getFlattenedNodes(nodes: FileNode[]): FileNode[] {
    const result: FileNode[] = []
    for (const node of nodes) {
      result.push(node)
      if (node.type === 'folder' && node.expanded && node.children) {
        result.push(...getFlattenedNodes(node.children))
      }
    }
    return result
  }
  
  // Remove child nodes from selection if parent folder is selected
  function removeChildNodesFromSelection(selection: Set<string>): Set<string> {
    const result = new Set<string>()
    const selectedFolders = new Set<string>()
    
    // First, identify all selected folders
    for (const nodeId of selection) {
      const node = findNodeById(fileTree, nodeId)
      if (node && node.type === 'folder') {
        selectedFolders.add(nodeId)
      }
    }
    
    // Then, add only nodes that are not children of selected folders
    for (const nodeId of selection) {
      let isChildOfSelectedFolder = false
      
      // Check if this node is a child of any selected folder
      for (const folderId of selectedFolders) {
        if (nodeId !== folderId && isNodeChildOf(nodeId, folderId)) {
          isChildOfSelectedFolder = true
          break
        }
      }
      
      if (!isChildOfSelectedFolder) {
        result.add(nodeId)
      }
    }
    
    return result
  }
  
  // Check if a node is a child of a folder
  function isNodeChildOf(nodeId: string, folderId: string): boolean {
    const folderNode = findNodeById(fileTree, folderId)
    if (!folderNode || folderNode.type !== 'folder') return false
    
    function checkChildren(nodes: FileNode[]): boolean {
      for (const node of nodes) {
        if (node.id === nodeId) return true
        if (node.type === 'folder' && node.children) {
          if (checkChildren(node.children)) return true
        }
      }
      return false
    }
    
    return folderNode.children ? checkChildren(folderNode.children) : false
  }
  
  // Navigate with keyboard
  async function navigateUp(withShift: boolean = false) {
    const flatNodes = getFlattenedNodes(fileTree)
    if (flatNodes.length === 0) return
    
    if (!focusedNodeId) {
      focusedNodeId = flatNodes[0].id
      if (!withShift) {
        selectedFiles.clear()
        selectedFiles.add(focusedNodeId)
        selectedFiles = new Set(selectedFiles)
        lastSelectedFile = focusedNodeId
      }
    } else {
      const currentIndex = flatNodes.findIndex(n => n.id === focusedNodeId)
      if (currentIndex > 0) {
        const previousNodeId = flatNodes[currentIndex - 1].id
        focusedNodeId = previousNodeId
        await tick() // Force UI update
        
        if (withShift) {
          // Multi-select with shift
          if (lastSelectedFile) {
            const lastIndex = flatNodes.findIndex(n => n.id === lastSelectedFile)
            const startIndex = Math.min(lastIndex, currentIndex - 1)
            const endIndex = Math.max(lastIndex, currentIndex - 1)
            
            // Clear previous selection and select range
            selectedFiles.clear()
            for (let i = startIndex; i <= endIndex; i++) {
              selectedFiles.add(flatNodes[i].id)
            }
            // Remove child nodes if their parent folders are selected
            selectedFiles = removeChildNodesFromSelection(selectedFiles)
          } else {
            selectedFiles.add(previousNodeId)
            selectedFiles = removeChildNodesFromSelection(selectedFiles)
            lastSelectedFile = previousNodeId
          }
        } else {
          // Single selection
          selectedFiles.clear()
          selectedFiles.add(previousNodeId)
          selectedFiles = new Set(selectedFiles)
          lastSelectedFile = previousNodeId
        }
      }
    }
    
    // Scroll into view
    requestAnimationFrame(() => {
      const focusedElement = document.querySelector(`[data-node-id="${focusedNodeId}"]`) as HTMLElement
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    })
    
    // Ensure the file explorer stays focused
    if (explorerEl && !hasFocus) {
      explorerEl.focus()
    }
  }
  
  async function navigateDown(withShift: boolean = false) {
    const flatNodes = getFlattenedNodes(fileTree)
    if (flatNodes.length === 0) return
    
    if (!focusedNodeId) {
      focusedNodeId = flatNodes[0].id
      if (!withShift) {
        selectedFiles.clear()
        selectedFiles.add(focusedNodeId)
        selectedFiles = new Set(selectedFiles)
        lastSelectedFile = focusedNodeId
      }
    } else {
      const currentIndex = flatNodes.findIndex(n => n.id === focusedNodeId)
      if (currentIndex < flatNodes.length - 1) {
        const nextNodeId = flatNodes[currentIndex + 1].id
        focusedNodeId = nextNodeId
        await tick() // Force UI update
        
        if (withShift) {
          // Multi-select with shift
          if (lastSelectedFile) {
            const lastIndex = flatNodes.findIndex(n => n.id === lastSelectedFile)
            const startIndex = Math.min(lastIndex, currentIndex + 1)
            const endIndex = Math.max(lastIndex, currentIndex + 1)
            
            // Clear previous selection and select range
            selectedFiles.clear()
            for (let i = startIndex; i <= endIndex; i++) {
              selectedFiles.add(flatNodes[i].id)
            }
            // Remove child nodes if their parent folders are selected
            selectedFiles = removeChildNodesFromSelection(selectedFiles)
          } else {
            selectedFiles.add(nextNodeId)
            selectedFiles = removeChildNodesFromSelection(selectedFiles)
            lastSelectedFile = nextNodeId
          }
        } else {
          // Single selection
          selectedFiles.clear()
          selectedFiles.add(nextNodeId)
          selectedFiles = new Set(selectedFiles)
          lastSelectedFile = nextNodeId
        }
      }
    }
    
    // Scroll into view
    requestAnimationFrame(() => {
      const focusedElement = document.querySelector(`[data-node-id="${focusedNodeId}"]`) as HTMLElement
      if (focusedElement) {
        focusedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    })
    
    // Ensure the file explorer stays focused
    if (explorerEl && !hasFocus) {
      explorerEl.focus()
    }
  }
  
  // Find node by ID recursively
  function findNodeById(nodes: FileNode[], id: string): FileNode | null {
    for (const node of nodes) {
      if (node.id === id) return node
      if (node.children) {
        const found = findNodeById(node.children, id)
        if (found) return found
      }
    }
    return null
  }
  
  // Handle keyboard events
  function handleExplorerKeydown(e: KeyboardEvent) {
    
    // Special handling for space key during editing
    if (e.key === ' ' && editingNodeId) {
      // Do nothing - let the input handle it
      return
    }
    
    // Don't handle keyboard events when editing
    if (editingNodeId) {
      return
    }
    
    const node = findNodeById(fileTree, focusedNodeId || '')
    
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        navigateUp(e.shiftKey)
        break
      case 'ArrowDown':
        e.preventDefault()
        navigateDown(e.shiftKey)
        break
      case ' ': // Space key to open/toggle
        e.preventDefault()
        if (node) {
          if (node.type === 'file') {
            selectFile(node)
          } else if (node.type === 'folder') {
            toggleFolder(node)
          }
        }
        break
      case 'F2':
        // F2: Rename (standard Windows/Linux convention)
        e.preventDefault()
        if (node) {
          startEditing(node)
        }
        break
      case 'Enter':
        // Enter: Start editing/rename mode
        e.preventDefault()
        if (node) {
          startEditing(node)
        }
        break
      case 'o':
        // Cmd/Ctrl+O: Open file or toggle folder
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault()
          if (node) {
            if (node.type === 'file') {
              selectFile(node)
            } else if (node.type === 'folder') {
              toggleFolder(node)
            }
          }
        }
        break
      case 'ArrowLeft':
        // Collapse folder or move to parent
        e.preventDefault()
        if (node && node.type === 'folder' && node.expanded) {
          toggleFolder(node)
        }
        break
      case 'ArrowRight':
        // Expand folder or move to first child
        e.preventDefault()
        if (node && node.type === 'folder' && !node.expanded) {
          toggleFolder(node)
        }
        break
      case 'Delete':
      case 'Backspace':
        // Delete with confirmation
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault()
          // Delete all selected files or the focused node
          deleteSelectedNodes()
        }
        break
    }
  }
  
  function isFileOpen(fileId: string): boolean {
    // Use the reactive fileStoreState to ensure re-renders
    const currentTabs = Array.from(fileStoreState.tabs.values())
    
    // Get vault path for normalization
    const vault = get(vaultStore)
    const vaultPath = vault?.vaultPath || ''
    
    // Normalize IDs - handle vault path differences
    const normalizeId = (id: string) => {
      let normalized = id.replace(/^\/+/, '').replace(/\.md$/, '')
      
      // If ID starts with vault path, remove it
      if (vaultPath && normalized.startsWith(vaultPath)) {
        normalized = normalized.substring(vaultPath.length).replace(/^\//, '')
      }
      
      return normalized
    }
    
    const normalizedFileId = normalizeId(fileId)
    
    return currentTabs.some(tab => {
      const normalizedTabId = normalizeId(tab.id)
      
      // Also check if fileId is relative and tab.id is absolute
      // or vice versa
      if (normalizedTabId === normalizedFileId) {
        return true
      }
      
      // Additional check: if one has vault path and other doesn't
      if (vaultPath) {
        const fileIdWithVault = vaultPath + '/' + normalizedFileId
        const tabIdWithVault = vaultPath + '/' + normalizedTabId
        
        if (normalizeId(fileIdWithVault) === normalizedTabId ||
            normalizedFileId === normalizeId(tabIdWithVault)) {
          return true
        }
      }
      
      return false
    })
  }
  
  
  function filterNodes(nodes: FileNode[], query: string): FileNode[] {
    if (!query) return nodes
    
    const lowerQuery = query.toLowerCase()
    
    return nodes.reduce<FileNode[]>((acc, node) => {
      if (node.type === 'folder') {
        const filteredChildren = filterNodes(node.children || [], query)
        if (filteredChildren.length > 0) {
          acc.push({
            ...node,
            expanded: true,
            children: filteredChildren
          })
        }
      } else if (node.name.toLowerCase().includes(lowerQuery)) {
        acc.push(node)
      }
      
      return acc
    }, [])
  }
  
  // Don't use $derived - it creates new array references and causes flicker
  // Manually update filteredTree only when needed
  let filteredTree = $state<FileNode[]>([])

  // Update filteredTree when search query changes
  $effect(() => {
    if (searchQuery) {
      filteredTree = filterNodes(fileTree, searchQuery)
    } else {
      // No search - use fileTree directly (same reference!)
      filteredTree = fileTree
    }
  })
  
  
  // Drag and Drop handlers
  function handleDragStart(node: FileNode, event: DragEvent) {
    // Prevent dragging while editing
    if (editingNodeId) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    // Allow both files and folders to be dragged

    // If the dragged item is not selected, clear selection and select only this item
    if (!selectedFiles.has(node.id)) {
      selectedFiles.clear()
      selectedFiles.add(node.id)
      selectedFiles = new Set(selectedFiles)
    }

    // Set dragged files/folders
    draggedFiles = new Set(selectedFiles)
    isDragging = true

    // Set drag data
    if (event.dataTransfer) {
      // Allow both copy and move for images to be dropped on editor
      event.dataTransfer.effectAllowed = node.isImage ? 'copyMove' : 'move'
      event.dataTransfer.setData('text/plain', Array.from(draggedFiles).join(','))
      // Also set file ID for drop on editor
      if (draggedFiles.size === 1) {
        event.dataTransfer.setData('text/file-id', Array.from(draggedFiles)[0])

        // If it's an image file, add image-specific data for editor drop
        if (node.isImage) {
          event.dataTransfer.setData('application/x-image-file', JSON.stringify({
            id: node.id,
            name: node.name,
            path: node.document?.path || node.id, // Use document.path or fallback to id (which is the file path)
            isImage: true
          }))
        }
      }
    }
  }
  
  let dragCounter = 0
  
  function handleDragOver(node: FileNode, event: DragEvent) {
    if (node.type !== 'folder' || draggedFiles.size === 0) return
    
    event.preventDefault()
    event.stopPropagation() // Stop bubbling to parent folders
    
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    
    // Only set drop target if it's different to avoid flickering
    if (dropTargetId !== node.id) {
      dropTargetId = node.id
    }
  }
  
  function handleDragLeave(node: FileNode, event?: DragEvent) {
    // Check if we're actually leaving the element
    if (event) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
      const x = event.clientX
      const y = event.clientY
      
      // Only clear if cursor is actually outside the element
      if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
        if (dropTargetId === node.id) {
          dropTargetId = null
        }
      }
    } else {
      // Fallback for when event is not provided
      if (dropTargetId === node.id) {
        dropTargetId = null
      }
    }
  }
  
  async function handleDrop(node: FileNode, event: DragEvent) {
    event.preventDefault()
    
    if (node.type !== 'folder' || draggedFiles.size === 0) return
    
    // Check if file is being dropped in the same parent folder
    const draggedNode = getAllNodes(fileTree).find(n => draggedFiles.has(n.id))
    if (draggedNode) {
      const parentOfDragged = findParentOfNode(fileTree, draggedNode.id)
      // If dropping in the same parent folder, just clear drag state without moving
      if (parentOfDragged?.id === node.id || (!parentOfDragged && node.id === 'root')) {
        draggedFiles.clear()
        dropTargetId = null
        dropPosition = null
        isDragging = false
        return
      }
    }
    
    // Check if any dragged folder is being moved into itself or its descendants
    function isDescendantOf(childId: string, parentId: string): boolean {
      function checkDescendant(nodes: FileNode[]): boolean {
        for (const n of nodes) {
          if (n.id === parentId) {
            // Found the parent, now check if childId is in its subtree
            return containsNode(n, childId)
          }
          if (n.type === 'folder' && n.children) {
            if (checkDescendant(n.children)) return true
          }
        }
        return false
      }
      
      function containsNode(parent: FileNode, targetId: string): boolean {
        if (parent.id === targetId) return true
        if (parent.type === 'folder' && parent.children) {
          for (const child of parent.children) {
            if (containsNode(child, targetId)) return true
          }
        }
        return false
      }
      
      return checkDescendant(fileTree)
    }
    
    // Prevent moving a folder into itself or its descendants
    for (const draggedId of draggedFiles) {
      if (draggedId === node.id || isDescendantOf(node.id, draggedId)) {
        console.warn('Cannot move folder into itself or its descendants')
        draggedFiles.clear()
        dropTargetId = null
        dropPosition = null
        return
      }
    }
    
    // Get target folder children for conflict checking
    const targetFolderChildren = new Map<string, boolean>() // name -> exists
    
    function getTargetFolderContents(nodes: FileNode[], targetId: string): void {
      for (const n of nodes) {
        if (n.id === targetId && n.type === 'folder' && n.children) {
          n.children.forEach(child => targetFolderChildren.set(child.name.toLowerCase(), true))
          return
        } else if (n.type === 'folder' && n.children) {
          getTargetFolderContents(n.children, targetId)
        }
      }
    }
    
    getTargetFolderContents(fileTree, node.id)
    
    // Function to generate unique name by appending numbers
    function getUniqueNameInFolder(baseName: string, existingNames: Map<string, boolean>): string {
      // Check if the name already exists
      if (!existingNames.has(baseName.toLowerCase())) {
        return baseName
      }
      
      // Generate new name with number suffix
      let counter = 1
      let newName = `${baseName} ${counter}`
      
      while (existingNames.has(newName.toLowerCase())) {
        counter++
        newName = `${baseName} ${counter}`
      }
      
      return newName
    }
    
    // Process each dragged item and rename if necessary
    const renamedItems = new Map<string, string>() // originalId -> newName
    
    for (const draggedId of draggedFiles) {
      const draggedNode = getAllNodes(fileTree).find(n => n.id === draggedId)
      if (draggedNode) {
        const uniqueName = getUniqueNameInFolder(draggedNode.name, targetFolderChildren)
        if (uniqueName !== draggedNode.name) {
          renamedItems.set(draggedId, uniqueName)
        }
        // Add the name to the set to avoid conflicts between dragged items
        targetFolderChildren.set(uniqueName.toLowerCase(), true)
      }
    }
    
    // Move files and folders to target folder
    const movedItems: FileNode[] = []
    
    // Function to update IDs recursively when moving items
    function updateItemIds(item: FileNode, newParentPath: string, newName?: string): FileNode {
      const itemName = newName || item.name
      const newPath = newParentPath + '/' + itemName
      
      if (item.type === 'file') {
        return { ...item, id: newPath, name: itemName }
      } else {
        // For folders, add 'folder:' prefix to ID and recursively update children
        const newFolderId = `folder:${newPath}`
        const updatedFolder: FileNode = {
          ...item,
          id: newFolderId,
          name: itemName,
          children: item.children?.map(child => 
            updateItemIds(child, newPath, undefined)  // Use newPath without prefix for children
          ) || []
        }
        return updatedFolder
      }
    }
    
    // Remove items from their current location and collect them
    function removeFromTree(tree: FileNode[], itemIds: Set<string>): FileNode[] {
      return tree.reduce<FileNode[]>((acc, n) => {
        if (itemIds.has(n.id)) {
          // Apply new name if item was renamed
          const newName = renamedItems.get(n.id)
          // Remove 'folder:' prefix from target folder ID if present
          const targetPath = node.id.startsWith('folder:') 
            ? node.id.slice(7) 
            : node.id
          const updatedItem = updateItemIds(n, targetPath, newName)
          movedItems.push(updatedItem)
          return acc
        } else if (n.type === 'folder' && n.children) {
          return [...acc, { ...n, children: removeFromTree(n.children, itemIds) }]
        }
        return [...acc, n]
      }, [])
    }
    
    // Add items to target folder
    function addToFolder(tree: FileNode[], targetId: string, items: FileNode[]): FileNode[] {
      return tree.map(n => {
        if (n.id === targetId && n.type === 'folder') {
          return { ...n, children: [...(n.children || []), ...items] }
        } else if (n.type === 'folder' && n.children) {
          return { ...n, children: addToFolder(n.children, targetId, items) }
        }
        return n
      })
    }
    
    // First, update the tree and collect moved items
    const updatedTree = removeFromTree(fileTree, draggedFiles)
    
    // Now process file system moves
    const movePromises: Promise<void>[] = []
    
    // Create a map to track original IDs for each moved item
    const originalIdMap = new Map<FileNode, string>()
    for (const draggedId of draggedFiles) {
      const item = movedItems.find(m => {
        // Match based on the renamed name or original name
        const renamedName = renamedItems.get(draggedId)
        return (renamedName && m.name === renamedName) || 
               (!renamedName && getAllNodes(fileTree).find(n => n.id === draggedId)?.name === m.name)
      })
      if (item) {
        originalIdMap.set(item, draggedId)
      }
    }
    
    // Process each moved item
    for (const movedItem of movedItems) {
      const originalId = originalIdMap.get(movedItem)
      if (!originalId) {
        console.warn('Could not find original ID for moved item:', movedItem)
        continue
      }
      
      if (movedItem.type === 'file') {
        // Move the file in the vault
        const oldPath = originalId.endsWith('.md') ? originalId : originalId + '.md'
        const newPath = movedItem.id.endsWith('.md') ? movedItem.id : movedItem.id + '.md'
        
        
        movePromises.push(
          vaultStore.renameFile(oldPath, newPath).then(() => {
            // Update tab if file is open
            const fileStoreState = fileStore.getState()
            if (fileStoreState.tabs.has(originalId)) {
              fileStore.updateTabPath(originalId, movedItem.id)
              // If file was renamed, update title too
              const renamedName = renamedItems.get(originalId)
              if (renamedName) {
                fileStore.updateTabTitle(movedItem.id, renamedName.replace(/\.md$/, ''))
              }
            }
          }).catch(err => {
            console.error(`Failed to move file ${oldPath} to ${newPath}:`, err)
          })
        )
      } else if (movedItem.type === 'folder') {
        // Move folder with all its contents
        const oldFolderPath = originalId.startsWith('folder:') ? originalId.slice(7) : originalId
        const newFolderPath = movedItem.id.startsWith('folder:') ? movedItem.id.slice(7) : movedItem.id
        
        
        movePromises.push(
          vaultStore.renameFolder(oldFolderPath, newFolderPath).then(() => {
            // Update any open tabs for files within this folder
            const fileStoreState = fileStore.getState()
            for (const [tabId, tab] of fileStoreState.tabs.entries()) {
              // Check if tab is within the moved folder
              if (tabId.startsWith(oldFolderPath + '/')) {
                const relativePath = tabId.slice(oldFolderPath.length + 1)
                const newTabPath = newFolderPath + '/' + relativePath
                fileStore.updateTabPath(tabId, newTabPath)
              }
            }
          }).catch(err => {
            console.error(`Failed to move folder ${oldFolderPath} to ${newFolderPath}:`, err)
          })
        )
      }
    }
    
    // Wait for all moves to complete
    await Promise.all(movePromises)
    
    // Update the file tree
    fileTree = updatedTree
    fileTree = addToFolder(fileTree, node.id, movedItems)
    
    // Clear drag state
    draggedFiles.clear()
    dropTargetId = null
    dropPosition = null
    isDragging = false
    selectedFiles.clear()
    selectedFiles = new Set(selectedFiles)
    
    // Save folder structure
    saveFolderStructure()
  }
  
  function handleDragEnd() {
    isDragging = false
    draggedFiles.clear()
    dropTargetId = null
    dropTargetRoot = false
    dropPosition = null
  }
  
  // Handle drop on root (file tree area)
  function handleRootDragOver(event: DragEvent) {
    if (draggedFiles.size === 0) return
    
    // Only allow drop on empty area, not on nodes
    const target = event.target as HTMLElement
    if (target.closest('.file-node') || target.closest('.folder-node')) {
      dropTargetRoot = false
      dropPosition = null
      return
    }
    
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    
    // Allow dropping anywhere in the file tree area, including empty space at bottom
    const treeElement = target.closest('.file-tree') as HTMLElement
    if (treeElement) {
      dropTargetRoot = true
      dropTargetId = null
      
      // Calculate drop position based on mouse position
      const rect = treeElement.getBoundingClientRect()
      const nodes = treeElement.querySelectorAll('.file-node, .folder-node')
      
      if (nodes.length === 0) {
        // No nodes, drop at position 0
        dropPosition = 0
      } else {
        // Calculate position based on mouse Y coordinate
        const mouseY = event.clientY
        let insertIndex = filteredTree.length // Default to end
        
        // Check if mouse is above first item
        const firstNode = nodes[0] as HTMLElement
        const firstNodeRect = firstNode.getBoundingClientRect()
        if (mouseY < firstNodeRect.top + firstNodeRect.height / 2) {
          insertIndex = 0
        } else {
          // Check position relative to other nodes
          for (let i = 0; i < nodes.length; i++) {
            const nodeEl = nodes[i] as HTMLElement
            const nodeRect = nodeEl.getBoundingClientRect()
            if (mouseY < nodeRect.bottom) {
              insertIndex = i + 1
              break
            }
          }
        }
        
        dropPosition = insertIndex
      }
    }
  }
  
  function handleRootDragLeave(event: DragEvent) {
    const target = event.target as HTMLElement
    if (!target.closest('.file-tree')) {
      dropTargetRoot = false
      dropPosition = null
    }
  }
  
  async function handleRootDrop(event: DragEvent) {
    // Only handle if not dropping on a folder
    const target = event.target as HTMLElement
    if (target.closest('.folder-node')) return
    
    event.preventDefault()
    
    // Check if files are already in root
    let allInRoot = true
    for (const draggedId of draggedFiles) {
      const parent = findParentOfNode(fileTree, draggedId)
      if (parent) {
        allInRoot = false
        break
      }
    }
    
    // If all dragged items are already in root, just clear drag state
    if (allInRoot) {
      draggedFiles.clear()
      dropTargetId = null
      dropTargetRoot = false
      dropPosition = null
      isDragging = false
      return
    }
    
    if (draggedFiles.size === 0) return
    
    // Get root level item names for conflict checking
    const rootItemNames = new Map<string, boolean>()
    fileTree.forEach(item => rootItemNames.set(item.name.toLowerCase(), true))
    
    // Function to generate unique name by appending numbers
    function getUniqueNameInFolder(baseName: string, existingNames: Map<string, boolean>): string {
      if (!existingNames.has(baseName.toLowerCase())) {
        return baseName
      }
      
      let counter = 1
      let newName = `${baseName} ${counter}`
      
      while (existingNames.has(newName.toLowerCase())) {
        counter++
        newName = `${baseName} ${counter}`
      }
      
      return newName
    }
    
    // Process each dragged item and rename if necessary
    const renamedItems = new Map<string, string>() // originalId -> newName
    
    for (const draggedId of draggedFiles) {
      // Skip if item is already at root level
      if (fileTree.some(n => n.id === draggedId)) {
        continue
      }
      
      const draggedNode = getAllNodes(fileTree).find(n => n.id === draggedId)
      if (draggedNode) {
        const uniqueName = getUniqueNameInFolder(draggedNode.name, rootItemNames)
        if (uniqueName !== draggedNode.name) {
          renamedItems.set(draggedId, uniqueName)
        }
        // Add the name to the set to avoid conflicts between dragged items
        rootItemNames.set(uniqueName.toLowerCase(), true)
      }
    }
    
    // Move files and folders to root
    const movedItems: FileNode[] = []
    
    // Function to update IDs recursively when moving to root
    function updateItemIds(item: FileNode, newName?: string): FileNode {
      const itemName = newName || item.name
      const newPath = itemName // Root level items have just the name as path
      
      if (item.type === 'file') {
        return { ...item, id: newPath, name: itemName }
      } else {
        // For folders at root, add 'folder:' prefix
        const newFolderId = `folder:${newPath}`
        
        // Update child IDs recursively
        function updateChildIds(children: FileNode[], parentPath: string): FileNode[] {
          return children.map(child => {
            const childPath = parentPath + '/' + child.name
            if (child.type === 'file') {
              return {
                ...child,
                id: childPath,
                children: undefined
              }
            } else {
              return {
                ...child,
                id: `folder:${childPath}`,
                children: child.children 
                  ? updateChildIds(child.children, childPath)
                  : []
              }
            }
          })
        }
        
        const updatedFolder: FileNode = {
          ...item,
          id: newFolderId,
          name: itemName,
          children: item.children 
            ? updateChildIds(item.children, newPath)
            : []
        }
        
        return updatedFolder
      }
    }
    
    // Remove items from their current location
    function removeFromTree(tree: FileNode[], itemIds: Set<string>): FileNode[] {
      return tree.reduce<FileNode[]>((acc, n) => {
        if (itemIds.has(n.id)) {
          // Apply new name if item was renamed
          const newName = renamedItems.get(n.id)
          const updatedItem = updateItemIds(n, newName)
          movedItems.push(updatedItem)
          return acc
        } else if (n.type === 'folder' && n.children) {
          const filteredChildren = removeFromTree(n.children, itemIds)
          return [...acc, { ...n, children: filteredChildren }]
        }
        return [...acc, n]
      }, [])
    }
    
    // First, update the tree and collect moved items
    const updatedTree = removeFromTree(fileTree, draggedFiles)
    
    // Now process file system moves
    const movePromises: Promise<void>[] = []
    
    // Create a map to track original IDs for each moved item
    const originalIdMap = new Map<FileNode, string>()
    for (const draggedId of draggedFiles) {
      const item = movedItems.find(m => {
        // Match based on the renamed name or original name
        const renamedName = renamedItems.get(draggedId)
        return (renamedName && m.name === renamedName) || 
               (!renamedName && getAllNodes(fileTree).find(n => n.id === draggedId)?.name === m.name)
      })
      if (item) {
        originalIdMap.set(item, draggedId)
      }
    }
    
    // Process each moved item
    for (const movedItem of movedItems) {
      const originalId = originalIdMap.get(movedItem)
      if (!originalId) {
        console.warn('Could not find original ID for moved item:', movedItem)
        continue
      }
      
      if (movedItem.type === 'file') {
        // Move the file to root in the vault
        const oldPath = originalId.endsWith('.md') ? originalId : originalId + '.md'
        const newPath = movedItem.id.endsWith('.md') ? movedItem.id : movedItem.id + '.md'
        
        
        movePromises.push(
          vaultStore.renameFile(oldPath, newPath).then(() => {
            // Update tab if file is open
            const fileStoreState = fileStore.getState()
            if (fileStoreState.tabs.has(originalId)) {
              fileStore.updateTabPath(originalId, movedItem.id)
              // If file was renamed, update title too
              const renamedName = renamedItems.get(originalId)
              if (renamedName) {
                fileStore.updateTabTitle(movedItem.id, renamedName.replace(/\.md$/, ''))
              }
            }
          }).catch(err => {
            console.error(`Failed to move file ${oldPath} to ${newPath}:`, err)
          })
        )
      } else if (movedItem.type === 'folder') {
        // Move folder with all its contents to root
        const oldFolderPath = originalId.startsWith('folder:') ? originalId.slice(7) : originalId
        const newFolderPath = movedItem.id.startsWith('folder:') ? movedItem.id.slice(7) : movedItem.id
        
        
        movePromises.push(
          vaultStore.renameFolder(oldFolderPath, newFolderPath).then(() => {
            // Update any open tabs for files within this folder
            const fileStoreState = fileStore.getState()
            for (const [tabId, tab] of fileStoreState.tabs.entries()) {
              // Check if tab is within the moved folder
              if (tabId.startsWith(oldFolderPath + '/')) {
                const relativePath = tabId.slice(oldFolderPath.length + 1)
                const newTabPath = newFolderPath + '/' + relativePath
                fileStore.updateTabPath(tabId, newTabPath)
              }
            }
          }).catch(err => {
            console.error(`Failed to move folder ${oldFolderPath} to root:`, err)
          })
        )
      }
    }
    
    // Wait for all moves to complete
    await Promise.all(movePromises)
    
    // Update tree - remove from their current location and add to root
    fileTree = updatedTree
    fileTree = [...fileTree, ...movedItems]
    
    // Clear drag state
    draggedFiles.clear()
    dropTargetId = null
    dropTargetRoot = false
    dropPosition = null
    isDragging = false
    selectedFiles.clear()
    selectedFiles = new Set(selectedFiles)
    
    // Save folder structure
    saveFolderStructure()
  }
  
  // Clear selection when clicking outside
  function clearSelection(event: MouseEvent) {
    const target = event.target as HTMLElement
    if (!target.closest('.file-node') && !target.closest('.folder-node')) {
      selectedFiles.clear()
      selectedFiles = new Set(selectedFiles)
      focusedNodeId = null // Also clear focus
    }
  }
  
  // Initialize selection when explorer gets focus
  export async function initializeFocus() {
    // Exit search mode if active
    if (searchMode) {
      searchMode = false
    }
    
    const flatNodes = getFlattenedNodes(fileTree)
    if (flatNodes.length > 0) {
      // Always reset and select first file when initializing
      focusedNodeId = null
      selectedFiles.clear()
      selectedFiles = new Set(selectedFiles)
      
      await tick() // Force UI update
      
      // Now set the new focus
      focusedNodeId = flatNodes[0].id
      selectedFiles.add(focusedNodeId)
      selectedFiles = new Set(selectedFiles)
      lastSelectedFile = focusedNodeId
      
      await tick() // Force UI update again
    }
    // Focus the explorer element
    if (explorerEl) {
      explorerEl.focus()
    }
  }
</script>


<svelte:window 
  onclick={(e) => { hideContextMenu(); clearSelection(e); }}
/>


<div 
  bind:this={explorerEl}
  class="file-explorer {className}" 
  class:dragging={isDragging}
  role="tree"
  aria-label="파일 탐색기"
  tabindex="0"
  onclick={(e) => {
    // Focus the explorer when clicking anywhere in the container
    const target = e.target as HTMLElement
    // Don't focus if clicking on input fields (during editing)
    if (!target.matches('input') && !target.matches('textarea')) {
      if (explorerEl && !hasFocus) {
        explorerEl.focus()
      }
    }
  }}
  onkeydown={handleExplorerKeydown}
  onfocus={() => {
    hasFocus = true

    // Exit search mode if active
    if (searchMode) {
      searchMode = false
    }
    
    // Initialize selection if none exists
    if (!focusedNodeId) {
      const flatNodes = getFlattenedNodes(fileTree)
      if (flatNodes.length > 0) {
        focusedNodeId = flatNodes[0].id
        selectedFiles.clear()
        selectedFiles.add(focusedNodeId)
        selectedFiles = new Set(selectedFiles)
        lastSelectedFile = focusedNodeId
      }
    }
  }}
  onblur={() => {
    hasFocus = false
  }}
  onfocusin={() => hasFocus = true}
  onfocusout={() => hasFocus = false}
>
  <!-- Loading Progress -->
  {#if isScanning && scanProgress}
    <div class="scan-progress">
      <div class="scan-progress-header">
        <span class="scan-title">Scanning vault...</span>
        <span class="scan-stats">{scanProgress.current} / {scanProgress.total}</span>
      </div>
      <div class="scan-progress-bar">
        <div 
          class="scan-progress-fill" 
          style="width: {Math.min(100, (scanProgress.current / Math.max(1, scanProgress.total)) * 100)}%"
        ></div>
      </div>
      <div class="scan-current-path">{scanProgress.currentPath}</div>
    </div>
  {/if}
  
  <!-- File Tree -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <div 
    bind:this={fileTreeEl}
    class="file-tree"
    class:scrolling={isScrolling}
    role="group"
    onclick={(e) => {
      // Focus the explorer when clicking on empty space
      const target = e.target as HTMLElement
      if (target.classList.contains('file-tree')) {
        if (explorerEl) {
          explorerEl.focus()
        }
      }
    }}
    ondragover={handleRootDragOver}
    ondragleave={handleRootDragLeave}
    ondrop={handleRootDrop}
    oncontextmenu={showBlankContextMenu}
    onscroll={() => {
      isScrolling = true
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
      scrollTimeout = setTimeout(() => {
        isScrolling = false
      }, 500) // Hide scrollbar 0.5 seconds after scrolling stops
    }}
  >
    {#if dropTargetRoot && dropPosition === 0}
      <div class="drop-indicator"></div>
    {/if}
    {#each filteredTree as node, index (index)}
      {#if node.type === 'folder'}
        <FolderNodeComponent
          {node}
          depth={0}
          isSelected={selectedFiles.has(node.id)}
          isFocused={focusedNodeId === node.id}
          isDragged={draggedFiles.has(node.id)}
          isDropTarget={dropTargetId === node.id}
          {selectedFiles}
          {focusedNodeId}
          {draggedFiles}
          {dropTargetId}
          {editingNodeId}
          {editingTitle}
          editingDuplicateWarning={editingDuplicateWarning}
          {isFileOpen}
          onToggle={() => toggleFolder(node)}
          onToggleFolder={toggleFolder}
          onSelect={(e) => {
            focusedNodeId = node.id;
            selectFile(node, e);
          }}
          onSelectFile={selectFile}
          onContextMenu={(e) => showContextMenu(node, e)}
          onDragStart={(e) => handleDragStart(node, e)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(node, e)}
          onDragLeave={(e) => handleDragLeave(node, e)}
          onDrop={(e) => handleDrop(node, e)}
          onStartEdit={startEditing}
          onFinishEdit={finishEditing}
          onEditKeydown={handleEditKeydown}
          onEditTitleChange={(value) => {
            editingTitle = value
            checkDuplicateName(value)
          }}
          onFileContextMenu={showContextMenu}
          onFileDragStart={handleDragStart}
          onFileDragEnd={handleDragEnd}
          onFolderDragOver={handleDragOver}
          onFolderDragLeave={handleDragLeave}
          onFolderDrop={handleDrop}
        />
      {:else}
        <RootFileWrapper
          {node}
          {selectedFiles}
          {focusedNodeId}
          {draggedFiles}
          {editingNodeId}
          {editingTitle}
          editingDuplicateWarning={editingDuplicateWarning}
          {isFileOpen}
          onSelectFile={selectFile}
          onContextMenu={showContextMenu}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onStartEdit={startEditing}
          onFinishEdit={finishEditing}
          onEditKeydown={handleEditKeydown}
          onEditTitleChange={(value) => {
            editingTitle = value
            checkDuplicateName(value)
          }}
        />
      {/if}
      {#if dropTargetRoot && dropPosition === index + 1}
        <div class="drop-indicator"></div>
      {/if}
    {/each}
    
    {#if filteredTree.length === 0 && !isVaultChanging && !isScanning}
      <div class="empty-state">
        {#if searchQuery}
          <p>검색 결과가 없습니다</p>
        {:else}
          <FileText size={32} />
          <p>문서가 없습니다</p>
          <button
            class="create-btn"
            onclick={createNewDocument}
          >
            첫 문서 만들기
          </button>
        {/if}
      </div>
    {:else if filteredTree.length === 0 && (isVaultChanging || isScanning)}
      <!-- Show nothing or a subtle loading indicator during vault change/scan -->
      <div class="vault-changing-placeholder">
        <!-- Empty or minimal content while loading -->
      </div>
    {/if}
  </div>
  
  <!-- Explorer Toolbar -->
  <div class="explorer-toolbar">
    <div class="vault-section">
      <VaultDropdown />
    </div>
    <div class="toolbar-actions">
      <button
        class="toolbar-btn"
        onclick={createNewDocument}
        title="새 파일"
      >
        <Plus size={16} strokeWidth={2} />
      </button>
      <button
        class="toolbar-btn"
        onclick={createNewFolder}
        title="새 폴더"
      >
        <Folder size={16} strokeWidth={2} />
      </button>
      <button
        class="toolbar-btn"
        class:disabled={!hasFolders}
        onclick={hasFolders ? toggleAllFolders : undefined}
        disabled={!hasFolders}
        title={!hasFolders ? '폴더가 없습니다' :
               folderExpansionLevel === 'all-expanded' ? '모든 폴더 접기' : 
               folderExpansionLevel === 'all-collapsed' ? '모든 폴더 펼치기' : 
               '모든 폴더 펼치기/접기'}
      >
        {#if folderExpansionLevel === 'all-expanded'}
          <ChevronsUp size={16} />
        {:else if folderExpansionLevel === 'all-collapsed'}
          <ChevronsDown size={16} />
        {:else}
          <ChevronsUpDown size={16} />
        {/if}
      </button>
    </div>
  </div>
  
  <!-- Context Menu (CSS fallback for web, not shown in Electron) -->
  {#if (contextMenuNode || contextMenuType === 'blank') && !isElectron}
    {@const isMultipleSelection = selectedFiles.size > 1}
    {@const isBulkOperation = contextMenuNode && isMultipleSelection && selectedFiles.has(contextMenuNode.id)}
    <div
      class="context-menu"
      role="menu"
      tabindex="-1"
      style="left: {contextMenuPosition.x}px; top: {contextMenuPosition.y}px"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => {
        if (e.key === 'Escape') {
          hideContextMenu()
        }
      }}
    >
      {#if contextMenuType === 'blank'}
        <!-- Blank space context menu -->
        <button
          class="context-item"
          onclick={() => {
            createNewFileInRoot()
            hideContextMenu()
          }}
        >
          <span class="context-icon">
            <Plus size={12} />
          </span>
          <span class="context-label">새 노트</span>
          <span class="context-shortcut">⌘N</span>
        </button>
        <button
          class="context-item"
          onclick={() => {
            createNewFolderInRoot()
            hideContextMenu()
          }}
        >
          <span class="context-icon">
            <Folder size={12} />
          </span>
          <span class="context-label">새 폴더</span>
        </button>
      {:else if contextMenuNode}
        <!-- Node context menu -->
        {#if contextMenuNode.type === 'folder' && !isBulkOperation}
          <button
            class="context-item"
            onclick={() => {
              createNewFile(contextMenuNode)
              hideContextMenu()
            }}
          >
            <span class="context-icon">
              <Plus size={12} />
            </span>
            <span class="context-label">새 노트</span>
          </button>
          <button
            class="context-item"
            onclick={() => {
              createNewFolder(contextMenuNode)
              hideContextMenu()
            }}
          >
            <span class="context-icon">
              <Folder size={12} />
            </span>
            <span class="context-label">새 폴더</span>
          </button>
          <div class="context-divider"></div>
          <button
            class="context-item"
            onclick={() => {
              toggleFolder(contextMenuNode)
              hideContextMenu()
            }}
          >
            <span class="context-icon">
              {#if contextMenuNode.expanded}
                <ChevronDown size={12} />
              {:else}
                <ChevronRight size={12} />
              {/if}
            </span>
            <span class="context-label">{contextMenuNode.expanded ? '폴더 접기' : '폴더 펼치기'}</span>
          </button>
        {/if}
        {#if !isBulkOperation}
          <button
            class="context-item"
            onclick={() => {
              startEditing(contextMenuNode)
              hideContextMenu()
            }}
          >
            <span class="context-icon">
              <Edit2 size={12} />
            </span>
            <span class="context-label">이름 변경</span>
            <span class="context-shortcut">⌘R</span>
          </button>
        {/if}
        <div class="context-divider"></div>
        <button
          class="context-item danger"
          onclick={() => {
            if (isBulkOperation) {
              deleteSelectedNodes()
            } else {
              deleteNode(contextMenuNode)
            }
            hideContextMenu()
          }}
        >
          <span class="context-icon">
            <Trash2 size={12} />
          </span>
          <span class="context-label">
            {#if isBulkOperation}
              {selectedFiles.size}개 항목 삭제
            {:else}
              삭제
            {/if}
          </span>
          <span class="context-shortcut">⌘⌫</span>
        </button>
      {/if}
    </div>
  {/if}
</div>

<style>
  .file-explorer {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--bg-secondary);
    font-size: var(--ui-font-size-md); /* 13px - one step up from sm(11px) */
    overflow: hidden; /* Ensure content doesn't overflow */
    position: relative;
  }
  
  /* Remove focus outline that causes orange border */
  .file-explorer:focus {
    outline: none;
  }
  
  .file-explorer:focus-visible {
    outline: none;
  }
  
  /* Remove animations for instant updates like Obsidian */
  .file-explorer-content {
    /* No transition for instant updates */
  }
  
  .file-node {
    /* No animation for instant rendering */
    /* transition: background-color 0.1s ease-out; */ /* Removed to prevent flashing */
  }
  
  .icon-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 3px;
    color: var(--text-secondary);
    cursor: pointer;
    flex-shrink: 0;
  }
  
  .icon-btn:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
  
  .file-tree {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 8px 0 24px 0; /* Increased bottom padding for better spacing */
    min-height: 0; /* Allow proper flexbox shrinking */
  }
  
  
  .file-explorer.dragging {
    cursor: grabbing;
  }
  
  .drop-indicator {
    height: 2px;
    background: var(--accent-primary);
    margin: 2px 12px;
    border-radius: 1px;
  }
  
  
  
  /* Context Menu - macOS Style */
  .context-menu {
    position: fixed;
    min-width: 200px;
    background: var(--bg-primary);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    box-shadow: 
      0 0 0 0.5px rgba(0, 0, 0, 0.05),
      0 3px 8px rgba(0, 0, 0, 0.1),
      0 10px 20px rgba(0, 0, 0, 0.08);
    padding: 5px;
    z-index: 10000; /* Increased to ensure it appears above sidebar border */
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }
  
  .context-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 4px 8px;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: var(--text-primary);
    cursor: default;
    text-align: left;
    font-size: 13px;
    transition: background-color 0.1s ease;
  }
  
  .context-item:hover {
    background: var(--accent-primary);
    color: white;
  }
  
  .context-item.danger {
    color: var(--error);
  }
  
  .context-item.danger:hover {
    background: var(--error);
    color: white;
  }
  
  .context-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    opacity: 0.7;
  }
  
  .context-label {
    flex: 1;
    font-weight: 400;
  }
  
  .context-shortcut {
    margin-left: auto;
    opacity: 0.5;
    font-size: 11px;
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  }
  
  .context-item:hover .context-icon,
  .context-item:hover .context-shortcut {
    opacity: 1;
  }
  
  .context-divider {
    height: 1px;
    background: rgba(0, 0, 0, 0.1);
    margin: 5px 8px;
  }
  
  /* Empty State */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px;
    color: var(--text-tertiary);
    gap: 12px;
  }
  
  /* Placeholder during vault change to prevent empty state flash */
  .vault-changing-placeholder {
    min-height: 100px;
    /* Keep the space but show nothing */
  }
  
  .create-btn {
    padding: 6px 12px;
    background: var(--accent-primary);
    color: white;
    border: none;
    border-radius: 4px;
    font-size: var(--ui-font-size-sm);
    cursor: pointer;
  }
  
  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  
  .modal {
    background: var(--bg-primary);
    border-radius: 8px;
    padding: 24px;
    width: 400px;
    max-width: 90vw;
  }
  
  /* Removed unused .modal h3 selector */
  
  .modal-input {
    width: 100%;
    padding: 8px 12px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-primary);
    border-radius: 4px;
    color: var(--text-primary);
    outline: none;
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 24px;
  }
  
  .modal-btn {
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    font-size: var(--ui-font-size-sm);
  }
  
  .modal-btn.cancel {
    background: var(--bg-tertiary);
    color: var(--text-secondary);
  }
  
  .modal-btn.primary {
    background: var(--accent-primary);
    color: white;
  }
  
  /* Scrollbar - hidden by default, shown only when actively scrolling */
  .file-tree {
    scrollbar-width: none; /* Hide scrollbar for Firefox by default */
  }
  
  .file-tree.scrolling {
    scrollbar-width: thin;
    scrollbar-color: var(--border-primary) transparent;
  }
  
  /* Webkit browsers (Chrome, Safari, Edge) */
  .file-tree::-webkit-scrollbar {
    width: 6px;
    display: none; /* Hide by default */
  }
  
  .file-tree.scrolling::-webkit-scrollbar {
    display: block; /* Show when scrolling */
  }
  
  .file-tree::-webkit-scrollbar-thumb {
    background: var(--border-primary);
    border-radius: 3px;
    background-clip: padding-box;
  }
  
  .file-tree::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Explorer Toolbar - Match footer height exactly */
  .explorer-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px 0 8px; /* Reduced left padding for left alignment */
    border-top: 1px solid var(--border-primary);
    background: var(--bg-secondary);
    min-height: 41px; /* Match footer height exactly */
    height: 41px;
    flex-shrink: 0;
  }
  
  .vault-section {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    margin-right: 8px;
    padding-left: 0;
  }
  
  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .toolbar-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--text-secondary);
    cursor: pointer;
  }
  
  .toolbar-btn:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-secondary);
    color: var(--text-primary);
  }
  
  .toolbar-btn:active:not(.disabled) {
    background: var(--bg-primary);
  }
  
  .toolbar-btn.disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .toolbar-btn.disabled:hover {
    background: transparent;
    border-color: transparent;
    color: var(--text-secondary);
  }
  
  .toolbar-separator {
    width: 1px;
    height: 20px;
    background: var(--border-secondary);
    margin: 0 4px;
    opacity: 0.5;
  }
  
  /* Responsive */
  @media (max-width: 768px) {
    .explorer-toolbar {
      padding: 6px 12px;
      min-height: 41px;
    }
  }
  
  /* Scan Progress */
  .scan-progress {
    padding: 12px 16px;
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-color);
    animation: slideDown 0.3s ease;
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .scan-progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    font-size: 0.9em;
  }
  
  .scan-title {
    color: var(--text-primary);
    font-weight: 500;
  }
  
  .scan-stats {
    color: var(--text-secondary);
    font-size: 0.85em;
  }
  
  .scan-progress-bar {
    width: 100%;
    height: 4px;
    background: var(--bg-secondary);
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 6px;
  }
  
  .scan-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--accent-primary), var(--accent-secondary, #6366f1));
    border-radius: 2px;
    transition: width 0.3s ease;
    animation: shimmer 1.5s infinite;
  }
  
  @keyframes shimmer {
    0% { opacity: 0.8; }
    50% { opacity: 1; }
    100% { opacity: 0.8; }
  }
  
  .scan-current-path {
    font-size: 0.8em;
    color: var(--text-tertiary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>

<script module lang="ts">
</script>
